import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AnalogStick from "./AnalogStick";
import DPad from "./DPad";
import { wanderPokemons } from "./pokemon";
import { getPlayerSpriteRowOffset } from "./player";
import "../styles/CaptureAnimations.css";
import { clamp } from "./helper";
import {
  boxesOverlap,
  rockOverlap,
  shrinkOverlap,
  shrinkRectOverlap,
  
} from "./collision";
import api from "../../../api";
import { mineLureImg, dropLureImg, pouchIcon, mineralPotionImg, POKEBALL_ITEM_ID, 
  GREATBALL_ITEM_ID, ULTRABALL_ITEM_ID, LONGBALL_ITEM_ID, RANGEBALL_ITEM_ID, MUDBALL_ITEM_ID, RUBYBALL_ITEM_ID,
   SAPPHIREBALL_ITEM_ID, EMERALDBALL_ITEM_ID, GOLDENBALL_ITEM_ID, DIAMONDBALL_ITEM_ID, SHADOWBALL_ITEM_ID } from "../../../constants/items";

// Import gem images for special ball rewards
import sapphireImg from "../../../assets/images/items/sapphire.png";
import rubyImg from "../../../assets/images/items/ruby.png";
import emeraldImg from "../../../assets/images/items/emerald.png";
import goldImg from "../../../assets/images/items/gold.png";
import diamondImg from "../../../assets/images/items/diamond.png";
import rockImg from "../../../assets/images/items/rock.png";

import PokemonLayer from "./PokemonLayer";
import RocksLayer from "./RockLayer";
import NPCLayer from "./NPCLayer";
import PortalLayer from "./PortalLayer";
import TeleportZonesLayer from "./TeleportZonesLayer";
import TrainersLayer from "./TrainersLayer";
import InventoryPanel from "../Components/InventoryPanel";
import HeldItemQuickSwap from "../Components/HeldItemQuickSwap";
import { rockIcons } from "../MapConfig/rockIcons";
import MineralPotionIndicator from "../Components/MineralPotionIndicator";
import ShinyPotionIndicator from "../Components/ShinyPotionIndicator";
import FishingAnimation from "../Components/FishingAnimation";
import FishingSpots from "../Components/FishingSpots";

import BattleScreen from "../../Battle/BattleScreen";
import DialogueModal from "../../../components/Modals/DialogueModal";
import RaffleModal from "../../../components/Modals/RaffleModal";
import LuckyDipModal from "../../../components/Modals/LuckyDipModal";
import CollectionContestModal from "../../../components/Modals/CollectionContestModal";
import AuctionHouseModal from "../../../components/Modals/AuctionHouseModal";
import RacetrackModal from "../../../components/Modals/RacetrackModal";
import WheelOfFortuneModal from "../../../components/Modals/WheelOfFortuneModal";
import BalloonPopModal from "../../../components/Modals/BalloonPopModal";
import TicketExchangeModal from "../../../components/Modals/TicketExchangeModal";
import DiglettDashModal from "../../../components/Modals/DiglettDashModal";
import PlayerShopLotModal from "../../../components/Modals/PlayerShopLotModal";
import PlayerShopBrowseModal from "../../../components/Modals/PlayerShopBrowseModal";
import PlayerShopManageModal from "../../../components/Modals/PlayerShopManageModal";
import ShopLotsLayer from "./ShopLotsLayer";
import DepartmentStoreModal from "../../../components/Modals/DepartmentStoreModal";
import IslandShopModal from "../../../components/Modals/IslandShopModal";

export default function GameMap({ config, onNpcInteraction, onPlayerPositionChange }) {
  const navigate = useNavigate();
  
  const {
    id: MAP_ID,
    image: mapImageUrl,
    over: overImageUrl,
    width: MAP_WIDTH,
    characterSprite: characterSpriteUrl,
    lureImages: { lureImg, superLureImg, ultraLureImg },
    height: MAP_HEIGHT,
    tileSize: TILE_SIZE,
    viewport: { width: VIEWPORT_WIDTH, height: VIEWPORT_HEIGHT },
    initialPlayer,
    collisionAreas,
    teleportZones,
    battleBackground,
  } = config;

  // —— States ——
  // Check for teleport target coordinates in localStorage
  const getTeleportTarget = () => {
    const teleportData = localStorage.getItem('teleportTarget');
    if (teleportData) {
      localStorage.removeItem('teleportTarget'); // Clear after use
      return JSON.parse(teleportData);
    }
    return null;
  };
  
  const teleportTarget = getTeleportTarget();
  const [playerPos, setPlayerPos] = useState({
    x: teleportTarget?.x || initialPlayer.x,
    y: teleportTarget?.y || initialPlayer.y,
  });
  const [direction, setDirection] = useState(initialPlayer.direction);
  const [playerStep, setPlayerStep] = useState(initialPlayer.step);
  const [isNight, setIsNight] = useState(false); // Day/night cycle state

  // Control type (analog stick or D-pad)
  const [controlType, setControlType] = useState(
    localStorage.getItem("controlType") || "analog"
  );

  // Listen for control type changes from Settings
  useEffect(() => {
    const handleStorageChange = () => {
      const newControlType = localStorage.getItem("controlType") || "analog";
      setControlType(newControlType);
    };

    // Listen for storage events (changes from other tabs/windows)
    window.addEventListener("storage", handleStorageChange);

    // Also check periodically in case same-tab changes don't trigger storage event
    const interval = setInterval(handleStorageChange, 1000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const [showPouch, setShowPouch] = useState(false);
  const togglePouch = () => setShowPouch((v) => !v);

  const stickActiveRef = useRef(false);
  const stickSeqTimeoutsRef = useRef([]);
  const miningPositionRef = useRef(null); // Ref to store mining position during animation
  const capturePositionRef = useRef(null); // Ref to store capture position during animation
  // Refs to provide latest state inside intervals/callbacks
  const playerPosRef = useRef(playerPos);
  const [expLureHits, setExpLureHits] = useState(0);
  const [dropLureHits, setDropLureHits] = useState(0);
  
  // Pickaxe max durability values
  const pickaxeMaxDurability = {
    228: 50,  // Normal pickaxe
    229: 200, // Ruby pickaxe
    230: 150, // Emerald pickaxe
    231: 75,  // Golden pickaxe
    232: 250, // Diamond pickaxe
    233: 200, // Sapphire pickaxe
  };

  const [rocks, setRocks] = useState([]);
  const [breakingFrames, setBreaking] = useState({});
  const [trainerStates, setTrainerStates] = useState([]);
  const [berryPlants, setBerryPlants] = useState([]);

  // Berry planting positions - matches backend config (island_positions.py)
  const BERRY_PLANT_POSITIONS = [
    [97, 378],   // Slot 0
    [111, 378],  // Slot 1
    [125, 378],  // Slot 2
    [178, 378],  // Slot 3
    [192, 378],  // Slot 4
    [206, 378],  // Slot 5
    [256, 378],  // Slot 6
    [270, 378],  // Slot 7
    [284, 378],  // Slot 8
    [338, 378],  // Slot 9
    [352, 378],  // Slot 10
    [366, 378],  // Slot 11
    [96, 4],
    [112, 4],
    [128, 4],
    [179, 4],
    [192, 4],
    [206, 4],
    [256, 4],
    [270, 4],
    [284, 4],
    [338, 4],
    [352, 4],
    [366, 4],  
  ];

  const [wildPokemon, setWildPokemon] = useState([]);
  const [floatingTexts, setFloatingTexts] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [steamPuffs, setSteamPuffs] = useState([]);
  const [portals, setPortals] = useState([]);

  const [inventory, setInventory] = useState([]);
  const [selectedBall, setSelectedBall] = useState(null);
  const [catchRates, setCatchRates] = useState([]);
  const [selectedAction, setSelectedAction] = useState(null);

  const [mineralPotionActive, setMineralPotionActive] = useState(false);
  const [mineralPotionExpire, setMineralPotionExpire] = useState(null);
  const [mineralPotionCountdown, setMineralPotionCountdown] = useState(0);
  const [shinyPotionActive, setShinyPotionActive] = useState(false);
  const [shinyPotionExpire, setShinyPotionExpire] = useState(null);
  const [shinyPotionCountdown, setShinyPotionCountdown] = useState(0);
  
  // Held Item Quick Swap state
  const [showHeldItemSwap, setShowHeldItemSwap] = useState(false);

  const [activeLure, setActiveLure] = useState(null);
  const [lureExpire, setLureExpire] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [gems, setGems] = useState({});
  const [shinyCharm, setShinyCharm] = useState(null);
  const [equippedPickaxe, setEquippedPickaxe] = useState(null);
  const [equippedRod, setEquippedRod] = useState(null);
  const [equippedItemFinder, setEquippedItemFinder] = useState(null);
  const [equippedCatchingGloves, setEquippedCatchingGloves] = useState(null);
  const [hasEquippedRing, setHasEquippedRing] = useState(false);
  const [hasDepartmentRing, setHasDepartmentRing] = useState(false);
  
  // Effect to close held item swap popup if ring is unequipped
  useEffect(() => {
    if (!hasEquippedRing && showHeldItemSwap) {
      setShowHeldItemSwap(false);
    }
  }, [hasEquippedRing, showHeldItemSwap]);
  const [mapScale, setMapScale] = useState(1);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inBattle, setInBattle] = useState(false);
  const [initialBattleData, setInitialBattleData] = useState(null);
  const [battleTowerVictory, setBattleTowerVictory] = useState(false);
  const [battleTowerFloor, setBattleTowerFloor] = useState(null);

  const ws = useRef(null);
  const expireTO = useRef(null);
  const reconnectTO = useRef(null);
  const [battleSpawnId, setBattleSpawnId] = useState(null);
  const [captureEffects, setCaptureEffects] = useState([]);
  
  // NPC interaction states
  const [dialogueModalOpen, setDialogueModalOpen] = useState(false);
  const [raffleModalOpen, setRaffleModalOpen] = useState(false);
  const [luckyDipModalOpen, setLuckyDipModalOpen] = useState(false);
  const [collectionContestModalOpen, setCollectionContestModalOpen] = useState(false);
  const [auctionHouseModalOpen, setAuctionHouseModalOpen] = useState(false);
  const [racetrackModalOpen, setRacetrackModalOpen] = useState(false);
  const [wheelOfFortuneModalOpen, setWheelOfFortuneModalOpen] = useState(false);
  const [balloonPopModalOpen, setBalloonPopModalOpen] = useState(false);
  const [ticketExchangeModalOpen, setTicketExchangeModalOpen] = useState(false);
  const [activeNPC, setActiveNPC] = useState(null);
  const [diglettDashModalOpen, setDiglettDashModalOpen] = useState(false);
  const [playerShopLotModalOpen, setPlayerShopLotModalOpen] = useState(false);
  const [playerShopBrowseModalOpen, setPlayerShopBrowseModalOpen] = useState(false);
  const [playerShopManageModalOpen, setPlayerShopManageModalOpen] = useState(false);
  const [selectedLotId, setSelectedLotId] = useState(null);
  const [selectedShopName, setSelectedShopName] = useState('');
  
  const [departmentStoreModalOpen, setDepartmentStoreModalOpen] = useState(false);
  const [islandShopModalOpen, setIslandShopModalOpen] = useState(false);
  const [storeItems, setStoreItems] = useState([]);
  const [currency, setCurrency] = useState({ money: 0, coins: 0 });
  const [quantityMap, setQuantityMap] = useState({});
  const [activeCategory, setActiveCategory] = useState('all');
  
  // Fishing state
  const [fishingSpots, setFishingSpots] = useState([]);
  const [isFishing, setIsFishing] = useState(false);
  const [fishingAnimation, setFishingAnimation] = useState(null);

  const refreshRocks = useCallback(() => {
    api
      .get("/v1/rocks", { params: { map_id: MAP_ID }, withCredentials: true })
      .then((r) => setRocks(r.data.data || []))
      .catch(console.error);
  }, [MAP_ID]);

  const refreshBerryPlants = useCallback(() => {
    if (MAP_ID === 1) {  // Only on Island map
      api
        .get("/v1/berry-plants/", { params: { map_id: MAP_ID }, withCredentials: true })
        .then((r) => setBerryPlants(r.data.data || []))
        .catch(console.error);
    }
  }, [MAP_ID]);

  // Fetch fishing spots for Island map
  useEffect(() => {
    if (MAP_ID === 1) {
      api.get('/v1/fishing/spots', { params: { map_id: 1 }, withCredentials: true })
        .then(response => {
          console.log('Fishing spots loaded:', response.data.spots);
          setFishingSpots(response.data.spots || []);
        })
        .catch(err => console.error('Error loading fishing spots:', err));
    }
  }, [MAP_ID]);

  // Handle fishing spot click
  const handleFishingSpotClick = async (spot, spotIndex) => {
    if (isFishing) return;
    
    // Check if player has any rod equipped (Old Rod 137, Good Rod 138, or Super Rod 139)
    if (!equippedRod || equippedRod.durability <= 0) {
      spawnText('You need a fishing rod equipped to fish!', playerPos.x, playerPos.y - 20, '#f87171');
      return;
    }
    
    // Validate rod item ID
    const validRodIds = [137, 138, 139];
    if (!validRodIds.includes(equippedRod.item)) {
      spawnText('You need a fishing rod equipped to fish!', playerPos.x, playerPos.y - 20, '#f87171');
      return;
    }
    
    setIsFishing(true);
    
    // Move player to fishing position
    setPlayerPos({ x: spot.player_x, y: spot.player_y });
    setDirection(spot.direction);
    
    try {
      const response = await api.post('/v1/fishing/use-rod', {
        rod_item_id: equippedRod.item, // Use the equipped rod's item ID
        spot_index: spotIndex,
        map_id: 1
      }, { withCredentials: true });
      
      if (response.data.success) {
        // Show fishing animation
        setFishingAnimation({
          x: spot.player_x,
          y: spot.player_y,
          direction: spot.direction,
          duration: response.data.duration_seconds * 1000
        });
        
        // Wait for animation to complete
        setTimeout(() => {
          setFishingAnimation(null);
          setIsFishing(false);
          
          // Handle reward
          if (response.data.reward_type === 'pokemon') {
            // Spawn Pokemon on map near the player based on fishing direction
            const pokemonName = response.data.reward_data.pokemon;
            spawnText(`A wild ${pokemonName} appeared!`, playerPos.x, playerPos.y - 20, '#4ade80');
            
            // Calculate Pokemon spawn position based on fishing direction
            let pokeX = playerPos.x;
            let pokeY = playerPos.y;
            
            switch(spot.direction) {
              case 'left':
                pokeX = playerPos.x - 32;
                break;
              case 'right':
                pokeX = playerPos.x + 32;
                break;
              case 'up':
                pokeY = playerPos.y - 32;
                break;
              case 'down':
                pokeY = playerPos.y + 32;
                break;
            }
            
            // Add Pokemon to wildPokemon array with all required properties
            setWildPokemon(prev => [...prev, {
              id: `fishing_${Date.now()}`,
              species: pokemonName,
              name: pokemonName,
              x: pokeX,
              y: pokeY,
              sprite: `/Maps/Overworld/${pokemonName}.png`,
              shiny: false,
              level: Math.floor(Math.random() * 10) + 5,
              direction: 'down',
              step: 0,
              capturing: false,
              captured: false
            }]);
            
          } else if (response.data.reward_type === 'fisher_coin') {
            const amount = response.data.reward_data.amount;
            spawnText(`+${amount} Fisher Coin!`, playerPos.x, playerPos.y - 20, '#fbbf24');
            
          } else if (response.data.reward_type === 'item') {
            const amount = response.data.reward_data.amount;
            spawnText(`+${amount} ${response.data.reward_data.item_name}!`, playerPos.x, playerPos.y - 20, '#a78bfa');
          }
          
          // Display fishing EXP gained
          if (response.data.fishing_exp) {
            setTimeout(() => {
              spawnText(`+${response.data.fishing_exp} Fishing EXP`, playerPos.x, playerPos.y - 40, '#60a5fa');
            }, 250);
          }
          
          // Update equipped rod durability locally
          setEquippedRod(rod => {
            if (!rod) return null;
            const newDurability = response.data.rod_durability;
            if (newDurability <= 0) {
              spawnText('Your Rod broke!', playerPos.x, playerPos.y - 60, '#f87171');
              // Reload inventory to reflect broken rod
              api.get("/v1/inventory", { withCredentials: true }).then((r) => {
                const filtered = r.data.filter(
                  (i) =>
                    [2, 3, 4, 137, 199,200,201,202,203, 234,167, 235, 116, 117, 6, 7, 8, 9, 10, 4000, 4001, 4002, 4003, 4004, 4005, 4006, 4007,4008, 214, 701780, 701781].includes(i.item) &&
                    i.amount > 0
                );
                setInventory(filtered);
              }).catch(console.error);
              return null;
            } else {
              // Get max durability based on rod type
              const maxDurability = rod.item === 137 ? 50 : rod.item === 138 ? 100 : 200;
              spawnText(`Rod: ${newDurability}/${maxDurability}`, playerPos.x, playerPos.y - 60, '#94a3b8');
              return { ...rod, durability: newDurability };
            }
          });
          
          // Refresh inventory and equipped items
          api.get("/v1/inventory", { withCredentials: true })
            .then((r) => {
              const filtered = r.data.filter(
                (i) =>
                  [2, 3, 4, 137, 199,200,201,202,203, 234,167, 235, 116, 117, 6, 7, 8, 9, 10, 4000, 4001, 4002, 4003, 4004, 4005, 4006, 4007,4008, 214, 701780, 701781].includes(i.item) &&
                  i.amount > 0
              );
              setInventory(filtered);
              
              // Refresh equipped rod (Old Rod 137, Good Rod 138, Super Rod 139)
              const updatedRod = r.data.find(i => [137, 138, 139].includes(i.item) && i.stack_amount === 1);
              setEquippedRod(updatedRod || null);
            }).catch(console.error);
        }, response.data.duration_seconds * 1000);
      }
    } catch (error) {
      setIsFishing(false);
      setFishingAnimation(null);
      const errorMsg = error.response?.data?.detail || 'Fishing failed!';
      spawnText(errorMsg, playerPos.x, playerPos.y - 20, '#f87171');
    }
  };

  // Initialize mineral potion status when component mounts
  useEffect(() => {
    // Check mineral potion status
    api.get("/v1/mineral-potion-status", { withCredentials: true })
      .then(response => {
        if (response.data.active) {
          const currentTime = Math.floor(Date.now() / 1000);
          const remainingSeconds = Math.max(0, response.data.expiration - currentTime);

          // Set mineral potion state
          setMineralPotionActive(true);
          setMineralPotionExpire(response.data.expiration);
          setMineralPotionCountdown(remainingSeconds);

          // Set up interval to update countdown
          const interval = setInterval(() => {
            setMineralPotionCountdown(prev => {
              if (prev <= 1) {
                clearInterval(interval);
                setMineralPotionActive(false);
                setMineralPotionExpire(null);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);

          // Store the interval reference for cleanup
          expireTO.current = interval;

          // Return cleanup function
          return () => {
            if (interval) {
              clearInterval(interval);
            }
          };
        }
      })
      .catch(error => {
        console.error('Error checking mineral potion status:', error);
      });
  }, []);

  // Handle Mineral Potion use with stacking support
  const handleMineralPotionUse = (expirationTimestamp) => {
    // Clear any existing countdown interval
    if (expireTO.current) {
      clearInterval(expireTO.current);
      expireTO.current = null;
    }

    // If there's an active potion, calculate remaining time to stack
    let newExpiration = expirationTimestamp;
    if (mineralPotionActive && mineralPotionExpire) {
      const currentTime = Math.floor(Date.now() / 1000);
      const remainingSeconds = Math.max(0, mineralPotionExpire - currentTime);

      // Stack the time by adding remaining seconds to the new expiration
      if (remainingSeconds > 0) {
        newExpiration = expirationTimestamp + remainingSeconds;
      }
    }

    setMineralPotionActive(true);
    setMineralPotionExpire(newExpiration);

    // Calculate remaining seconds
    const currentTime = Math.floor(Date.now() / 1000);
    const remainingSeconds = Math.max(0, newExpiration - currentTime);
    setMineralPotionCountdown(remainingSeconds);

    // Refresh rocks to show the mineral effect
    refreshRocks();

    // Set up interval to update countdown
    const interval = setInterval(() => {
      setMineralPotionCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setMineralPotionActive(false);
          setMineralPotionExpire(null);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Store the interval reference for cleanup
    expireTO.current = interval;

    // Return cleanup function
    return () => {
      if (expireTO.current) {
        clearInterval(expireTO.current);
        expireTO.current = null;
      }
    };
  };

  // Handle battle end
  const handleBattleEnd = (result) => {
    // If this was a Battle Tower battle and player won
    if (initialBattleData?.opponent_type === "battle_tower" && result?.winner === "player") {
      // Set victory state to trigger the post-battle flow
      setBattleTowerVictory(true);

      // Only show Magma Coin message if player actually earned one (first-time floor completion)
      if (result?.item_drop === "Magma Coin") {
        // Show victory message with Magma Coin
        spawnText(
          "Victory! Earned 1 Magma Coin!",
          playerPos.x,
          playerPos.y - 32,
          "gold",
          "/Magma Coin.png"
        );
      } else {
        // Regular victory message without Magma Coin for repeat clears
        spawnText(
          "Victory!",
          playerPos.x,
          playerPos.y - 32,
          "gold"
        );
      }

      // Set a timeout to close the battle and show teleport message
      setTimeout(() => {
        setInBattle(false);
        setInitialBattleData(null);

        // Show teleport message
        spawnText(
          `Teleporting to floor ${battleTowerFloor + 1} in 3 seconds...`,
          playerPos.x,
          playerPos.y - 16,
          "cyan"
        );

        // Set another timeout to navigate to the next floor
        setTimeout(() => {
          // Reset victory state
          setBattleTowerVictory(false);

          // Navigate to the next floor (this would typically be handled by the BattleTower component)
          if (config.onBattleVictory) {
            // Check if this is Maxie (floor 100) to pass boss parameter
            const boss = battleTowerFloor === 100 ? 'maxie' : null;
            config.onBattleVictory(battleTowerFloor, boss);
          }
        }, 3000);
      }, 1000);
    } else if (initialBattleData?.opponent_type === "battle_tower" && result?.winner === "foe") {

      setInBattle(false);
      setInitialBattleData(null);

      spawnText(
        "Defeat! Resetting to checkpoint...",
        playerPos.x,
        playerPos.y - 16,
        "red"
      );

      // Call the Battle Tower's loss handler if available
      if (config.onBattleLoss) {
        const floor = result?.floor || initialBattleData?.battle_tower_floor || 1;
        config.onBattleLoss(floor);
      }
    } else {
      // For non-Battle Tower battles, just close the battle screen
      setWildPokemon(wp => wp.filter(p => p.id !== battleSpawnId))
      setInBattle(false);
      setInitialBattleData(null);
    }
  };

  // Handle NPC interactions
  const handleNPCClick = async (npc) => {
    if (config.onNPCClick && (npc.type === "grunt" || npc.type === "maxie" || npc.type === "groudon" || npc.type === "minute" || npc.type === "endless")) {
      // For Battle Tower NPCs, trigger battle directly
      if (npc.type === "grunt" || npc.type === "minute" || npc.type === "endless") {
        try {
          // Check if player has any Pokemon with HP > 0
          const r = await api.get("/v1/roster", { withCredentials: true });
          const anyAlive = r.data.some((p) => p.current_hp > 0);
          if (!anyAlive) {
            spawnText(
              "All your Pokémon are fainted! You can't battle.",
              playerPos.x,
              playerPos.y - 16,
              "red"
            );
            return;
          }

          // Get the floor from the config or default to 1
          const floor = config.floor || 1;

          // Initialize battle with the backend, similar to trainer battles
          try {
            // Determine tower type based on NPC type
            let tower_type = "original"; // default
            if (npc.type === "minute") {
              tower_type = "60second";
            } else if (npc.type === "endless") {
              tower_type = "endless";
            }

            const res = await api.post(
              "/v1/battle/start",
              {
                opponent_type: "battle_tower",
                battle_tower_floor: floor,
                tower_type: tower_type
              },
              { withCredentials: true }
            );

            // Set battle data from backend response and trigger battle screen
            setInitialBattleData({
              ...res.data,
              opponent_type: "battle_tower"
            });
            setBattleTowerFloor(floor);
            setInBattle(true);
            return;
          } catch (err) {
            console.error("Failed to start Battle Tower battle:", err);
            const msg = err.response?.data?.detail || 
                       err.response?.data?.message || 
                       "Can't battle right now!";
            spawnText(msg, playerPos.x, playerPos.y - 16, "red");
            return;
          }
        } catch (err) {
          console.error("Error checking roster for Battle Tower battle:", err);
          spawnText("Error starting battle", playerPos.x, playerPos.y - 16, "red");
          return;
        }
      }else if(npc.type === "maxie"){
        try {
          // Check if player has any Pokemon with HP > 0
          const r = await api.get("/v1/roster", { withCredentials: true });
          const anyAlive = r.data.some((p) => p.current_hp > 0);
          if (!anyAlive) {
            spawnText(
              "All your Pokémon are fainted! You can't battle.",
              playerPos.x,
              playerPos.y - 16,
              "red"
            );
            return;
          }

          // Get the floor from the config or default to 1
          const floor = config.floor || 1;

          // Initialize battle with the backend, similar to trainer battles
          try {
            // Determine tower type based on NPC type
            let tower_type = "original"; // default
            if (npc.type === "minute") {
              tower_type = "60second";
            } else if (npc.type === "endless") {
              tower_type = "endless";
            }

            const res = await api.post(
              "/v1/battle/start",
              {
                opponent_type: "battle_tower",
                battle_tower_floor: floor,
                tower_type: tower_type
              },
              { withCredentials: true }
            );

            // Set battle data from backend response and trigger battle screen
            setInitialBattleData({
              ...res.data,
              opponent_type: "battle_tower"
            });
            setBattleTowerFloor(floor);
            setInBattle(true);
            return;
          } catch (err) {
            console.error("Failed to start Battle Tower battle:", err);
            const msg = err.response?.data?.detail || 
                       err.response?.data?.message || 
                       "Can't battle right now!";
            spawnText(msg, playerPos.x, playerPos.y - 16, "red");
            return;
          }
        } catch (err) {
          console.error("Error checking roster for Battle Tower battle:", err);
          spawnText("Error starting battle", playerPos.x, playerPos.y - 16, "red");
          return;
        }
      } else {
        // For other Battle Tower NPCs (maxie, groudon), use the custom handler
        config.onNPCClick(npc);
        return;
      }
    }
    // If external handler is provided, call it first
    if (onNpcInteraction && npc.id) {
      onNpcInteraction(npc.id, playerPos);
    }
    
    // If user has Ring of Department equipped, show a market emoji for shop-related NPCs
    
    if (hasDepartmentRing && (
      // Check for shop-related modal types
      npc.modalType === 'auctionhouse' || 
      npc.modalType === 'playerShopLot' || 
      npc.modalType === 'playerShopBrowse' || 
      npc.modalType === 'playerShopManage' ||
      // Check for shop-related NPC types
      npc.type === 'shop' ||
      npc.type === 'promo' ||
      npc.type === 'craft' ||
      npc.type === 'event' ||
      npc.type === 'trainer-potion' ||
      npc.type === 'trainer-ev' ||
      npc.type === 'tm-trainer'
    )) {
      // Create a market emoji floating text
      const id = Date.now() + Math.random();
      setFloatingTexts((ft) => [
        ...ft,
        { id, text: '🛒', x: npc.x || playerPos.x, y: (npc.y || playerPos.y) - 20, color: '#ffd700', created: Date.now() },
      ]);
      setTimeout(() => {
        setFloatingTexts((ft) => ft.filter((f) => f.id !== id));
      }, 2500);
    }
    
    if (npc.modalType) {
      // Handle modal-opening NPCs
      setActiveNPC(npc);
      switch (npc.modalType) {
        case 'dialogue':
          setDialogueModalOpen(true);
          break;
        case 'raffle':
          setRaffleModalOpen(true);
          break;
        case 'luckyDip':
          setLuckyDipModalOpen(true);
          break;
        case 'collection-contest':
          setCollectionContestModalOpen(true);
          break;
        case 'auctionhouse':
          setAuctionHouseModalOpen(true);
          break;
        case 'racetrack':
          setRacetrackModalOpen(true);
          break;
        case 'wheeloffortune':
          setWheelOfFortuneModalOpen(true);
          break;
          case 'diglettdash':
            setDiglettDashModalOpen(true);
            break;
        case 'ballon-pop':
          setBalloonPopModalOpen(true);
          break;
        case 'ticket-exchange':
          setTicketExchangeModalOpen(true);
          break;
        case 'playerShopLot':
          // For empty lots, open the lot purchase modal
          setSelectedLotId(npc.lotId);
          setPlayerShopLotModalOpen(true);
          break;
        case 'playerShopBrowse':
          // For other players' shops, open the browse modal
          setSelectedLotId(npc.lotId);
          setSelectedShopName(npc.name);
          setPlayerShopBrowseModalOpen(true);
          break;
        case 'playerShopManage':
          setSelectedLotId(npc.lotId);
          setPlayerShopManageModalOpen(true);
          break;
        case 'island':
          setIslandShopModalOpen(true);
          break;
        // Add more modal types as needed
        default:
          console.log(`Unknown modal type: ${npc.modalType}`);
      }
    } else if (npc.dialogue) {
      // Simple dialogue NPCs
      setActiveNPC(npc);
      setDialogueModalOpen(true);
    } else {
      // Custom action NPCs
      if (npc.action && typeof npc.action === 'function') {
        npc.action();
      } else {
        // Default behavior - spawn floating text
        spawnText(npc.defaultText || "Hello there!", playerPos.x, playerPos.y - 20, "#ffffff");
      }
    }
  };

  // Helper to map ball item IDs to CSS class names
  const getBallClass = (ballItemId) => {
    switch (Number(ballItemId)) {
      case POKEBALL_ITEM_ID: return 'pokeball';
      case GREATBALL_ITEM_ID: return 'greatball';
      case ULTRABALL_ITEM_ID: return 'ultraball';
      case LONGBALL_ITEM_ID: return 'longball';
      case RANGEBALL_ITEM_ID: return 'rangeball';
      case MUDBALL_ITEM_ID: return 'mudball';
      case RUBYBALL_ITEM_ID: return 'rubyball';
      case SAPPHIREBALL_ITEM_ID: return 'sapphireball';
      case EMERALDBALL_ITEM_ID: return 'emeraldball';
      case GOLDENBALL_ITEM_ID: return 'goldenball';
      case DIAMONDBALL_ITEM_ID: return 'diamondball';
      case SHADOWBALL_ITEM_ID: return 'shadowball';
      default: return 'pokeball';
    }
  };

  // —— Teleport Detection ——
  const checkTeleport = useCallback((x, y) =>
    teleportZones?.find(zone =>
      x < zone.x + zone.width &&
      x + 32 > zone.x &&
      y < zone.y + zone.height &&
      y + 32 > zone.y
    ),
  [teleportZones]);

  const handleTeleport = useCallback((targetFloor, targetX, targetY) => {
    console.log(`Teleporting to ${targetFloor} at (${targetX}, ${targetY})`);
    // Store target coordinates in localStorage for the destination map
    localStorage.setItem('teleportTarget', JSON.stringify({ x: targetX, y: targetY }));
    // Navigate to target map
    const targetMapName = targetFloor.charAt(0).toUpperCase() + targetFloor.slice(1);
    navigate(`/map/${targetMapName}`);
  }, [navigate]);


  // —— Helpers: sort inventory so lures come last, etc. —— 
  const sortedInv = [...inventory].sort((a, b) => {
    const isL = (id) => [6,7,8,9,10,234, 235,167, 214,116,117, 701780, 701781].includes(id);
    return isL(a.item) === isL(b.item) ? 0 : isL(a.item) ? 1 : -1;
  });

  function clearStickSeq() {
    stickSeqTimeoutsRef.current.forEach((id) => clearTimeout(id));
    stickSeqTimeoutsRef.current = [];
  }
  
  // Keep latest player position in a ref for wandering/callbacks
  useEffect(() => {
    playerPosRef.current = playerPos;
  }, [playerPos]);
  
  // Collision check against collisionAreas - wrapped in useCallback to maintain stable reference
  const isColliding = React.useCallback((x, y) => {
    const pw = 32,
      ph = 32;
    return collisionAreas.some((w) => {
      const padX = w.padX ?? 0;
      const padY = w.padY ?? 0;
      return shrinkRectOverlap(
        x, y, pw, ph,
        w.x + padX, w.y + padY, w.width - padX * 2, w.height - padY * 2
      );
    });
  }, [collisionAreas]);
  
  // Keep latest collision function in a ref to avoid stale closures in intervals
  const isCollidingRef = useRef(isColliding);
  useEffect(() => {
    isCollidingRef.current = isColliding;
  }, [isColliding]);
  
  // —— D-Pad movement (keyboard-style) ——
  const lastDPadMoveRef = useRef(0);
  const handleDPad = React.useCallback(({ x, y }) => {
    // Throttle D-pad input to prevent too-fast movement
    const now = Date.now();
    if (now - lastDPadMoveRef.current < 150) {
      return; // Ignore if less than 150ms since last move
    }
    
    const moving = Math.abs(x) >= 0.1 || Math.abs(y) >= 0.1;
    if (!moving) {
      setPlayerStep(0);
      return;
    }

    lastDPadMoveRef.current = now;

    let nx = playerPos.x;
    let ny = playerPos.y;
    let dir = direction;

    // Move by full TILE_SIZE like keyboard
    if (Math.abs(x) > Math.abs(y)) {
      dir = x > 0 ? "right" : "left";
      nx += (x > 0 ? 1 : -1) * TILE_SIZE;
    } else {
      dir = y > 0 ? "down" : "up";
      ny += (y > 0 ? 1 : -1) * TILE_SIZE;
    }

    nx = clamp(nx, 0, MAP_WIDTH - 32);
    ny = clamp(ny, 0, MAP_HEIGHT - 32);

    const hitRock = rocks
      .filter((r) => !r.collected)
      .some((r) => rockOverlap(nx, ny, r.x, r.y));
    const hitWall = isColliding(nx, ny);
    
    const anyCapturing = wildPokemon.some(p => p.capturing);
    const anyMining = Object.keys(breakingFrames).length > 0;
    
    const hitPokemon = !anyCapturing && !anyMining && wildPokemon.some(p => {
      const dist = Math.hypot(nx - p.x, ny - p.y);
      return dist < 24;
    });

    const hitTrainer = trainerStates.some(trainer => {
      const isStatic = Number(trainer.wander) === 0;
      const trainerWidth = isStatic ? 16 : 32;
      const trainerHeight = isStatic ? 24 : 32;
      
      return shrinkRectOverlap(
        nx, ny, 32, 32,
        trainer.x, trainer.y, trainerWidth, trainerHeight,
        8, 8, 4, 4
      );
    });
    
    const hitNPC = config.npcs && config.npcs.some(npc => {
      return shrinkRectOverlap(
        nx, ny, 32, 32,
        npc.x, npc.y, 16, 24,
        8, 8, 4, 4
      );
    });
    
    if (!hitRock && !hitWall && !hitPokemon && !hitTrainer && !hitNPC) {
      const teleportZone = checkTeleport(nx, ny);
      if (teleportZone) {
        handleTeleport(teleportZone.targetFloor, teleportZone.targetX, teleportZone.targetY);
        return;
      }
      
      setPlayerPos({ x: nx, y: ny });
      setDirection(dir);
      
      if (onPlayerPositionChange) {
        onPlayerPositionChange({ x: nx, y: ny });
      }
      
      // Animate walk cycle
      const walkSeq = [1, 2, 0];
      walkSeq.forEach((step, i) =>
        setTimeout(() => setPlayerStep(step), i * 150)
      );
    }
  }, [playerPos, direction, rocks, isColliding, wildPokemon, breakingFrames, trainerStates, config.npcs, checkTeleport, handleTeleport, onPlayerPositionChange, TILE_SIZE, MAP_WIDTH, MAP_HEIGHT]);

  // —— Analog‐stick movement —— 
  // Using useCallback to preserve the function reference across renders
  // This prevents the function from losing context during capture animations
  const handleStick = React.useCallback(({ x, y }) => {
    const TH = 0.1,
      speed = 0.2;
    const moving = Math.abs(x) >= TH || Math.abs(y) >= TH;

    if (!moving) {
      if (stickActiveRef.current) {
        stickActiveRef.current = false;
        clearStickSeq();
        setPlayerStep(0);
      }
      return;
    }

    let nx = playerPos.x,
      ny = playerPos.y,
      dir = direction;
    if (Math.abs(x) > Math.abs(y)) {
      dir = x > 0 ? "right" : "left";
      nx += (x > 0 ? 1 : -1) * TILE_SIZE * speed;
    } else {
      dir = y > 0 ? "down" : "up";
      ny += (y > 0 ? 1 : -1) * TILE_SIZE * speed;
    }
    nx = clamp(nx, 0, MAP_WIDTH - 32);
    ny = clamp(ny, 0, MAP_HEIGHT - 32);



    const hitRock = rocks
      .filter((r) => !r.collected)
      .some((r) => rockOverlap(nx, ny, r.x, r.y));
    const hitWall = isColliding(nx, ny);
    
    // Check for Pokemon collisions, but ONLY when not capturing or mining
    // This allows walking through Pokemon only during active capture/mining animations
    const anyCapturing = wildPokemon.some(p => p.capturing);
    const anyMining = Object.keys(breakingFrames).length > 0;
    
    // Only check Pokemon collisions if we're not capturing or mining
    const hitPokemon = !anyCapturing && !anyMining && wildPokemon.some(p => {
      const dist = Math.hypot(nx - p.x, ny - p.y);
      return dist < 12; // Collision radius with Pokemon
    });

    // Check for trainer collisions - prevent walking through trainers
    const hitTrainer = trainerStates.some(trainer => {
      // For static trainers (wander=0), use 16x24 dimensions
      const isStatic = Number(trainer.wander) === 0;
      const trainerWidth = isStatic ? 16 : 32;
      const trainerHeight = isStatic ? 24 : 32;
      
      // Use shrinkRectOverlap for precise collision detection
      return shrinkRectOverlap(
        nx, ny, 32, 32,
        trainer.x, trainer.y, trainerWidth, trainerHeight,
        8, 8, 4, 4 // Add some padding to make collision feel natural
      );
    });
    
    // Check for NPC collisions - prevent walking through NPCs
    const hitNPC = config.npcs && config.npcs.some(npc => {
      return shrinkRectOverlap(
        nx, ny, 32, 32, // Player hitbox
        npc.x, npc.y, 16, 24, // NPC hitbox (16x24)
        8, 8, 4, 4 // Padding for natural feel
      );
    });
    
    // Check for berry plant collisions - prevent walking through plants and empty slots
    let hitBerryPlant = false;
    if (MAP_ID === 1) {
      // Check planted berries
      hitBerryPlant = berryPlants.some(plant => {
        return shrinkRectOverlap(
          nx, ny, 32, 32, // Player hitbox
          plant.x, plant.y, 16, 16, // Berry plant hitbox (16x16)
          8, 8, 4, 4 // Padding for natural feel
        );
      });
      
      // Also check empty planting spots (all 12 positions)
      if (!hitBerryPlant) {
        const occupiedSlots = berryPlants.map(p => p.slot_index);
        
        hitBerryPlant = BERRY_PLANT_POSITIONS.some(([x, y], index) => {
          // Only check collision for empty slots
          if (occupiedSlots.includes(index)) return false;
          return shrinkRectOverlap(
            nx, ny, 32, 32, // Player hitbox
            x, y, 16, 16, // Empty slot hitbox (16x16)
            8, 8, 4, 4 // Padding for natural feel
          );
        });
      }
    }
    
    if (!hitRock && !hitWall && !hitPokemon && !hitTrainer && !hitNPC && !hitBerryPlant) {
      // Check for teleport before updating position
      const teleportZone = checkTeleport(nx, ny);
      if (teleportZone) {
        handleTeleport(teleportZone.targetFloor, teleportZone.targetX, teleportZone.targetY);
        return;
      }
      
      // Only move if we don't hit any obstacle (wall, rock, or Pokemon)
      // Unless we're currently capturing or mining, in which case Pokemon collisions are ignored
      setPlayerPos({ x: nx, y: ny });
      setDirection(dir);
      
      // Notify parent component about position change if callback exists
      if (onPlayerPositionChange) {
        onPlayerPositionChange({ x: nx, y: ny });
      }
      
      if (!stickActiveRef.current) {
        clearStickSeq();
        [1, 2, 0].forEach((step, i) => {
          const id = setTimeout(() => setPlayerStep(step), i * 150);
          stickSeqTimeoutsRef.current.push(id);
        });
        stickActiveRef.current = true;
      } else {
        setPlayerStep((prev) => (prev + 1) % 3);
      }
    }
  }, [playerPos, direction, rocks, isColliding, TILE_SIZE, MAP_WIDTH, MAP_HEIGHT, wildPokemon, breakingFrames, trainerStates, config.npcs, checkTeleport, handleTeleport, onPlayerPositionChange]);

  // This comment is left intentionally to mark where the duplicate isColliding function was removed

  

  // —— Recompute scale on window resize —— 
  useEffect(() => {
    function updateScale() {
      const scaleX = window.innerWidth / 336;
      const scaleY = window.innerHeight / 240;
      const scale = Math.min(scaleX, scaleY);
      setMapScale(Math.min(Math.max(scale, 1), 2));
    }
    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, []);


  useEffect(() => {
  api
    .get(`/v1/maps/${MAP_ID}/trainers`, { withCredentials: true })
    .then(r => setTrainers(r.data))
    .catch(console.error);
}, [MAP_ID]);

useEffect(() => {
    setTrainerStates(trainers.map(t => ({
      ...t,
      dirIndex: 0,      // 0=right,1=down,2=left,3=up
      moved:    0,      // px moved along current side
      stepFrame: 0      // animation frame (0–2)
    })));
  }, [trainers]);

  // Function to refresh equipped items
  const refreshEquippedItems = useCallback(async () => {
    try {
      const r = await api.get("/v1/inventory", { withCredentials: true });
      const allItems = r.data || [];

      // Find shiny charm (item ID 55)
      const charm = allItems.find(
        (i) => i.item === 194 && i.stack_amount > 0
      );
      setShinyCharm(charm || null);

      // Find equipped pickaxe (items 228-233)
      const pickaxe = allItems.find(
        (i) =>
          [228, 229, 230, 231, 232, 233].includes(i.item) &&
          i.stack_amount === 1
      );
      setEquippedPickaxe(pickaxe || null);

      // Find equipped fishing rod (Old Rod 137, Good Rod 138, Super Rod 139)
      const rod = allItems.find(
        (i) => [137, 138, 139].includes(i.item) && i.stack_amount === 1
      );
      setEquippedRod(rod || null);

      // Find equipped item finder (item ID 56)
      const itemFinder = allItems.find(
        (i) => i.item === 56 && i.stack_amount > 0
      );
      setEquippedItemFinder(itemFinder || null);
      
      // Find equipped catching gloves (item ID 113)
      const catchingGloves = allItems.find(
        (i) => i.item === 113 && i.stack_amount > 0
      );
      setEquippedCatchingGloves(catchingGloves || null);
      
      // Check for Equipment Ring (item ID 28)
      const equipmentRing = allItems.find(
        (i) => i.item === 28 && i.stack_amount === 1
      );
      setHasEquippedRing(!!equipmentRing);
      
      // Check for Ring of Department (item ID 140)
      // Look for the ring with stack_amount === 1 (equipped state)
      const departmentRing = allItems.find(
        (i) => i.item === 140 && i.stack_amount === 1
      );
      console.log('Ring of Department check:', { departmentRing, allItems });
      setHasDepartmentRing(!!departmentRing);
    } catch (error) {
      console.error("Failed to refresh equipped items:", error);
    }
  }, []);

  // —— Fetch initial data: catch rates, inventory, lure status, rocks —— 
  useEffect(() => {
    api
      .get("/v1/catchrate", { withCredentials: true })
      .then((r) => setCatchRates(r.data))
      .catch(console.error);

    // Call the refresh function to load equipped items
    refreshEquippedItems();

    api
      .get("/v1/inventory", { withCredentials: true })
      .then((r) => {
        //console.log("SelectedBall in localStorage:", localStorage.getItem("selectedBall"));
        // filter inventory down to balls & lures, berries, etc.
        const filtered = r.data.filter((i) =>
          [2, 3, 4, 199,200,201,202,203, 234,167, 235, 116, 117, 6, 7, 8, 9, 10, 4000, 4001, 4002, 4003, 4004, 4005, 4006, 4007,4008, 214, 701780, 701781].includes(i.item) &&
          i.amount > 0
        );
        setInventory(filtered);
        const saved = localStorage.getItem("selectedBall");
        if (saved) {
          const savedId = parseInt(saved, 10);
          const found = filtered.find((i) => i.item === savedId && i.amount > 0);
          if (found) {
            setSelectedBall(found);
          } 
        }
      }).catch(console.error).finally(() => {
        setInventoryLoading(false);
      });

    api
      .get("/v1/lure-status", { withCredentials: true })
      .then((r) => {
        if (r.data.lure) {
          setActiveLure("lure");
          setLureExpire(r.data.expiration);
        } else if (r.data.super_lure) {
          setActiveLure("super_lure");
          setLureExpire(r.data.expiration);
        } else if (r.data.ultra_lure) {
          setActiveLure("ultra_lure");
          setLureExpire(r.data.expiration);
        }
        setExpLureHits(r.data.exp_lure || 0);
        setDropLureHits(r.data.drop_lure || 0);
      })
      .catch(console.error);
// Check for Mineral Potion status using the new endpoint
api
.get("/v1/mineral-potion-status", { withCredentials: true })
.then((r) => {
  if (r.data.active) {
    const currentTime = Math.floor(Date.now() / 1000);
    const remainingSeconds = Math.max(0, r.data.expiration - currentTime);

    if (remainingSeconds > 0) {
      setMineralPotionActive(true);
      setMineralPotionExpire(r.data.expiration);
      setMineralPotionCountdown(remainingSeconds);

      // Set up interval to update countdown
      if (expireTO.current) {
        clearInterval(expireTO.current);
      }

      const interval = setInterval(() => {
        setMineralPotionCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setMineralPotionActive(false);
            setMineralPotionExpire(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      expireTO.current = interval;
    } else {
      setMineralPotionActive(false);
      setMineralPotionExpire(null);
      setMineralPotionCountdown(0);
    }
  } else {
    setMineralPotionActive(false);
    setMineralPotionExpire(null);
    setMineralPotionCountdown(0);
  }
})
.catch(error => {
  console.error('Error checking mineral potion status:', error);
  setMineralPotionActive(false);
  setMineralPotionExpire(null);
  setMineralPotionCountdown(0);
});

// Check for Shiny Potion status
api
.get("/v1/shiny-potion-status", { withCredentials: true })
.then((r) => {
  if (r.data.active) {
    const currentTime = Math.floor(Date.now() / 1000);
    const remainingSeconds = Math.max(0, r.data.expiration - currentTime);

    if (remainingSeconds > 0) {
      setShinyPotionActive(true);
      setShinyPotionExpire(r.data.expiration);
      setShinyPotionCountdown(remainingSeconds);

      // Set up interval to update countdown
      const shinyInterval = setInterval(() => {
        setShinyPotionCountdown(prev => {
          if (prev <= 1) {
            clearInterval(shinyInterval);
            setShinyPotionActive(false);
            setShinyPotionExpire(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Store interval ID for cleanup
      return () => clearInterval(shinyInterval);
    } else {
      setShinyPotionActive(false);
      setShinyPotionExpire(null);
      setShinyPotionCountdown(0);
    }
  } else {
    setShinyPotionActive(false);
    setShinyPotionExpire(null);
    setShinyPotionCountdown(0);
  }
})
.catch(error => {
  console.error('Error checking shiny potion status:', error);
  setShinyPotionActive(false);
  setShinyPotionExpire(null);
  setShinyPotionCountdown(0);
});
    api
      .get("/v1/rocks", { params: { map_id: MAP_ID }, withCredentials: true })
      .then((r) => setRocks(r.data.data || []))
      .catch(console.error);

    // Load berry plants on Island map
    if (MAP_ID === 1) {
      refreshBerryPlants();
    }

      api
   .get("/v1/mining/info", { withCredentials: true })
   .then(r => {
     if (r.data.success) {
       setGems(r.data.resources);
     }
   })
   .catch(console.error);
  }, [MAP_ID, refreshEquippedItems, refreshBerryPlants]);


  useEffect(() => {
    if (localStorage.getItem("selectedRock") === "true") {
      setSelectedAction({ type: "item", payload: "rock" });
    }
  }, []);

  // Auto-refresh berry plants every 30 seconds on Island map
  useEffect(() => {
    if (MAP_ID === 1) {
      const interval = setInterval(() => {
        refreshBerryPlants();
      }, 30000); // 30 seconds
      
      return () => clearInterval(interval);
    }
  }, [MAP_ID, refreshBerryPlants]);
  

  //
  // ── PERSIST “selectedBall” TO localStorage ────────────────────────────────────────
  //
  // 1) When `inventory` first arrives (or changes), check localStorage for a saved ball ID.
  //    If it’s still in inventory (amount>0), re‐apply it to state. Otherwise clear it.
  //
  useEffect(() => {
    if (inventoryLoading) return;
    const savedId = localStorage.getItem("selectedBall");
    if (savedId && inventory.length) {
      const match = inventory.find((i) => i.item.toString() === savedId);
  
     
      if (match && match.amount > 0) {
    
        if (!selectedBall || selectedBall.item !== match.item) {
          setSelectedBall(match);
          
        }
      } else {
        
        //console.log("Selected ball not found in current map inventory — keeping selection.");
      }
    }
  }, [inventory,inventoryLoading]);

  //
  // 2) Whenever selectedBall in state changes, write it (or remove) in localStorage.
  //
  useEffect(() => {
    if (selectedBall) {
      localStorage.setItem("selectedBall", selectedBall.item.toString());
    } 
  }, [selectedBall]);
  
  useEffect(() => {
    if (selectedBall) {
      setSelectedAction({ type: 'ball', payload: selectedBall });
    }
  }, [selectedBall]);

  //
  // 3) If inventory updates causes the selected‐ball’s amount to drop to 0 (e.g. just used the last one),
  //    we clear both the React state and the localStorage entry. We do that inside any place inventory changes:
  //
  //    – Already, we deduct one ball in capture(). So right after we do setInventory(…), we’ll inspect if
  //      that made selectedBall.amount === 0, and if so clear it. Below in capture() we’ll add that logic.
  //

  // —— Fetch gems when pouch opens —— 
  useEffect(() => {
    if (!showPouch) return;
    api
      .get("/v1/mining/info", { withCredentials: true })
      .then((r) => {
        if (r.data.success) {
          setGems(r.data.resources); 
        }
      })
      .catch(console.error);
  }, [showPouch]);

  // —— Lure countdown —— 
  useEffect(() => {
    if (!lureExpire) {
      setCountdown(0);
      return;
    }
    const iv = setInterval(() => {
      const rem = lureExpire - Math.floor(Date.now() / 1000);
      if (rem <= 0) {
        setActiveLure(null);
        setLureExpire(null);
        setCountdown(0);
        clearInterval(iv);
      } else {
        setCountdown(rem);
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [lureExpire]);

  // —— Pokemon wandering effect —— 
  useEffect(() => {
    const iv = setInterval(() => {
      const currentPlayer = playerPosRef.current;
      const collisionFn = isCollidingRef.current;
      setWildPokemon((prev) =>
        wanderPokemons(prev, TILE_SIZE, MAP_WIDTH, MAP_HEIGHT, currentPlayer, collisionFn)
      );
    }, 250);
    return () => clearInterval(iv);
  }, [TILE_SIZE, MAP_WIDTH, MAP_HEIGHT]);  // Uses refs for latest player/collision; restart if map/tile changes


   // —— Steam effects for EmberSpa map or maps with showSteamEffects enabled —— 
   useEffect(() => {

    // Return if not Ember Spa and showSteamEffects is not enabled
    if (MAP_ID !== 12 && (!config || !config.showSteamEffects)) return;

    // Use provided steam points or default Ember Spa points
    const steamPoints = config.steamPoints || [
      // Hot spring areas distributed throughout the map
      { x: 180, y: 180 }, { x: 200, y: 185 }, { x: 220, y: 175 }, { x: 240, y: 180 },
      { x: 280, y: 190 }, { x: 300, y: 185 }, { x: 320, y: 195 },
      { x: 190, y: 140 }, { x: 250, y: 145 }, { x: 310, y: 150 },
      { x: 170, y: 220 }, { x: 210, y: 225 }, { x: 270, y: 230 }, { x: 320, y: 235 },
      { x: 150, y: 200 }, { x: 350, y: 210 }, { x: 200, y: 160 }, { x: 290, y: 170 },
      // Additional scattered steam points
      { x: 160, y: 250 }, { x: 300, y: 260 }, { x: 230, y: 120 }, { x: 340, y: 180 },
      { x: 120, y: 190 }, { x: 380, y: 220 }, { x: 250, y: 280 }, { x: 180, y: 300 }
    ];

    let puffIdCounter = 0;
    const intervals = [];

    const createSteamPuff = (x, y) => {
      const puffId = puffIdCounter++;
      const randomOffset = {
        x: (Math.random() - 0.5) * 8, // Small random horizontal offset
        y: (Math.random() - 0.5) * 4  // Small vertical offset
      };

      const newPuff = {
        id: puffId,
        x: x + randomOffset.x,
        y: y + randomOffset.y,
        size: 3 + Math.random() * 2, // 3-5px size (smaller than before)
        opacity: 0.6 + Math.random() * 0.4, // 0.6-1.0 opacity
        duration: 1500 + Math.random() * 1000, // 1.5-2.5 seconds
        startTime: Date.now()
      };

      setSteamPuffs(prev => [...prev, newPuff]);

      // Remove puff after animation completes
      setTimeout(() => {
        setSteamPuffs(prev => prev.filter(puff => puff.id !== puffId));
      }, newPuff.duration + 100);
    };

    // Create intervals for each steam point
    steamPoints.forEach(point => {
      const interval = setInterval(() => {
        createSteamPuff(point.x, point.y);
      }, 800 + Math.random() * 1200); // Random interval 0.8-2.0 seconds
      intervals.push(interval);
    });

    return () => {
      intervals.forEach(clearInterval);
    };
  }, [MAP_ID, config]);

  useEffect(() => {
    const onKey = (e) => {
      let nx = playerPos.x,
        ny = playerPos.y,
        dir = direction;
      switch (e.key) {
        case "ArrowUp":
        case "w":
        case "W":
          ny -= TILE_SIZE;
          dir = "up";
          break;
        case "ArrowDown":
        case "s":
        case "S":
          ny += TILE_SIZE;
          dir = "down";
          break;
        case "ArrowLeft":
        case "a":
        case "A":
          nx -= TILE_SIZE;
          dir = "left";
          break;
        case "ArrowRight":
        case "d":
        case "D":
          nx += TILE_SIZE;
          dir = "right";
          break;
        default:
          return;
      }
      e.preventDefault();
      nx = clamp(nx, 0, MAP_WIDTH - 32);
      ny = clamp(ny, 0, MAP_HEIGHT - 32);
      console.log(nx, ny)
      const hitWall = isColliding(nx, ny);
      const hitRock = rocks
        .filter((r) => !r.collected)
        .some((r) => rockOverlap(nx, ny, r.x, r.y));
        
      // Check for Pokemon collisions, but ONLY when not capturing or mining
      // This allows walking through Pokemon only during active capture/mining animations
      const anyCapturing = wildPokemon.some(p => p.capturing);
      const anyMining = Object.keys(breakingFrames).length > 0;
      
      // Only check Pokemon collisions if we're not capturing or mining
      const hitPokemon = !anyCapturing && !anyMining && wildPokemon.some(p => {
        const dist = Math.hypot(nx - p.x, ny - p.y);
        return dist < 24; // Collision radius with Pokemon
      });

      // Check for trainer collisions - prevent walking through trainers
      const hitTrainer = trainerStates.some(trainer => {
        // For static trainers (wander=0), use 16x24 dimensions
        const isStatic = Number(trainer.wander) === 0;
        const trainerWidth = isStatic ? 16 : 32;
        const trainerHeight = isStatic ? 24 : 32;
        
        // Use shrinkRectOverlap for precise collision detection
        return shrinkRectOverlap(
          nx, ny, 32, 32,
          trainer.x, trainer.y, trainerWidth, trainerHeight,
          8, 8, 4, 4 // Add some padding to make collision feel natural
        );
      });
      
      // Check for NPC collisions - prevent walking through NPCs
      const hitNPC = config.npcs && config.npcs.some(npc => {
        // Using 16x24 dimensions for NPCs as specified
        return shrinkRectOverlap(
          nx, ny, 32, 32, // Player hitbox
          npc.x, npc.y, 16, 16, // NPC hitbox (16x24 as specified)
          8, 8, 4, 4 // Add some padding to make collision feel natural
        );
      });

      // Check for berry plant collisions - prevent walking through plants and empty slots
      let hitBerryPlant = false;
      if (MAP_ID === 1) {
        // Check planted berries
        hitBerryPlant = berryPlants.some(plant => {
          return shrinkRectOverlap(
            nx, ny, 32, 32, // Player hitbox
            plant.x, plant.y, 16, 16, // Berry plant hitbox (16x16)
            8, 8, 4, 4 // Padding for natural feel
          );
        });
        
        // Also check empty planting spots (all 12 positions)
        if (!hitBerryPlant) {
          const occupiedSlots = berryPlants.map(p => p.slot_index);
          
          hitBerryPlant = BERRY_PLANT_POSITIONS.some(([x, y], index) => {
            // Only check collision for empty slots
            if (occupiedSlots.includes(index)) return false;
            return shrinkRectOverlap(
              nx, ny, 32, 32, // Player hitbox
              x, y, 16, 16, // Empty slot hitbox (16x16)
              8, 8, 4, 4 // Padding for natural feel
            );
          });
        }
      }

      // Allow movement if not hitting any obstacles
      if (!hitWall && !hitRock && !hitPokemon && !hitTrainer && !hitNPC && !hitBerryPlant) {
        // Check for teleport before updating position
        const teleportZone = checkTeleport(nx, ny);
        if (teleportZone) {
          handleTeleport(teleportZone.targetFloor, teleportZone.targetX, teleportZone.targetY);
          return;
        }

        setPlayerPos({ x: nx, y: ny });
        setDirection(dir);

        if (e.repeat) {
          setPlayerStep((prev) => (prev + 1) % 3);
        } else {
          const walkSeq = [1, 2, 0];
          walkSeq.forEach((step, i) =>
            setTimeout(() => setPlayerStep(step), i * 150)
          );
        }
      }
    };
    window.addEventListener("keydown", onKey);
    
    return () => window.removeEventListener("keydown", onKey);
  }, [playerPos, direction, playerStep, wildPokemon, rocks, isColliding, TILE_SIZE, MAP_WIDTH, MAP_HEIGHT, breakingFrames]);


    useEffect(() => {
   if (selectedAction?.type === "item" && selectedAction.payload === "rock") {
      localStorage.setItem("selectedRock", "true");
    } else {
      localStorage.removeItem("selectedRock");
    }
    if (selectedAction?.type === "item" && selectedAction.payload === "rock") {
      setSelectedBall(null);
      localStorage.removeItem("selectedBall");
    }
  }, [selectedAction]);

  // —— Camera —— 
  const camX = clamp(playerPos.x - VIEWPORT_WIDTH / 2, 0, MAP_WIDTH - VIEWPORT_WIDTH);
  const camY = clamp(playerPos.y - VIEWPORT_HEIGHT / 2, 0, MAP_HEIGHT - VIEWPORT_HEIGHT);

  // —— Catching —— 
  const getPixelDist = (id) => {
    const b = catchRates.find((c) => c.pokeball_id === id);
    return b?.pixel_distance || 0;
  };

 
const handlePokemonClick = async (poke) => {
  // 1) If already mid-capture, do nothing
  if (poke.capturing) return;

  // Store the player position at the moment of interaction
  // This fixes the issue with analog stick movement during capture
  capturePositionRef.current = { ...playerPos };
  
  // 2) If rock is selected, check roster & enter battle
  if (selectedAction?.type === 'item' && selectedAction.payload === 'rock') {
    // a) First: fetch your roster & ensure someone has HP > 0
    try {
      const r = await api.get('/v1/roster', { withCredentials: true });
      const rosterData = r.data;
      const anyAlive = rosterData.some(p => p.current_hp > 0);
      if (!anyAlive) {
        spawnText(
          "All your Pokémon are fainted! You can't battle.",
          playerPos.x,
          playerPos.y - 16,
          "red"
        );
        return;
      }
    } catch (err) {
      console.error("Failed to fetch roster:", err);
      spawnText(
        "Could not check your roster. Try again later.",
        playerPos.x,
        playerPos.y - 16,
        "red"
      );
      return;
    }

    // b) Do we have at least one rock?
    const rockCount = gems.rock ?? 0;
    if (rockCount < 1) {
      spawnText(
        "No rocks to throw at the Pokémon!",
        playerPos.x,
        playerPos.y - 16,
        "red"
      );
      return;
    }

    try {
      // c) Deduct one rock locally (we’ll refund on failure)
      setGems(prev => ({ ...prev, rock: prev.rock - 1 }));

      // d) Attempt to start the battle on the server
      const battleRequest = {
        opponent_type: 'wild',
        spawn_id: poke.id,
        map_id: MAP_ID
      };
      
      // If this is a fishing Pokemon, include the species
      if (poke.id && poke.id.toString().startsWith('fishing_')) {
        battleRequest.species = poke.species || poke.name;
      }
      
      const res = await api.post(
        '/v1/battle/start',
        battleRequest,
        { withCredentials: true }
      );

      // e) On success: store the initial battle payload & mount BattleScreen
      setBattleSpawnId(poke.id);
      setInitialBattleData({ ...res.data, opponent_type: "wild" });
      setInBattle(true);

    } catch (err) {
      // f) On failure: refund the rock locally & show error
      setGems(prev => ({ ...prev, rock: prev.rock + 1 }));
      const msg = err.response?.data?.message || "Your rock missed!";
      spawnText(
        msg,
        playerPos.x,
        playerPos.y - 16,
        "red"
      );
    }

    return;
  }

  // 3) If a Poké-ball is selected, do your normal capture flow
  if (selectedAction?.type === "ball") {
    const ball = selectedAction.payload;
    const dist = Math.hypot(playerPos.x - poke.x, playerPos.y - poke.y);
    if (dist > getPixelDist(ball.item)) {
      spawnText(
        "Too far away!",
        playerPos.x,
        playerPos.y - 16,
        "red"
      );
      return;
    }

    // mark this Pokémon as “capturing” so the UI shows the ball animation
    setWildPokemon(prev =>
      prev.map(p =>
        p.id === poke.id
          ? {
              ...p,
              capturing:      true,
              captureFrame:   0,
              originalSprite: p.sprite,
              usedBallItemId: ball.item
            }
          : p
      )
    );
    // actually fire off the capture logic
    capture(poke, ball);
    return;
  }

  // 4) Nothing selected
  spawnText(
    "Select a Pokéball or rock first!",
    playerPos.x,
    playerPos.y - 16,
    "red"
  );
};
  
  
  

  async function capture(poke, ball) {
    // Save the player position at the moment of capture
    // Note: We already set capturePositionRef.current in handlePokemonClick
    // This ensures proper position tracking even during analog stick movement
    // We still keep a local copy for fallback
    const capturePosition = capturePositionRef.current || { ...playerPos };
    
    // Check if this is a fishing Pokemon (client-side spawned)
    const isFishingPokemon = poke.id && poke.id.toString().startsWith('fishing_');
    
    try {
      let res;
      
      if (isFishingPokemon) {
        // Handle fishing Pokemon capture - save to backend
        try {
          const captureRes = await api.post(
            "/v1/fishing/capture",
            { 
              species: poke.species, 
              ball_type: ball.item,
              level: poke.level || 5
            },
            { withCredentials: true }
          );
          res = captureRes;
        } catch (error) {
          // If backend call fails, still allow local capture
          console.error("Fishing capture backend error:", error);
          res = {
            data: {
              success: true,
              exp_gained: Math.floor(Math.random() * 50) + 50, // 50-100 XP
              pokedex_entry: false,
              reward_item: null
            }
          };
        }
      } else {
        // Normal server-side Pokemon capture
        res = await api.post(
          "/v1/capture",
          { mapId: MAP_ID, pokeId: poke.id, species: poke.species, ball_type: ball.item },
          { withCredentials: true }
        );
      }
  
      // Deduct one ball from inventory (and clear selectedBall if it hit 0)
      setInventory((inv) => {
        const updated = inv.map((i) =>
          i.item === ball.item ? { ...i, amount: i.amount - 1 } : i
        );
        const usedBall = updated.find((i) => i.item === ball.item);
        if (!usedBall || usedBall.amount <= 0) {
          if (selectedBall?.item === ball.item) {
            localStorage.removeItem("selectedBall");
            setSelectedBall(null);
          }
          return updated.filter((i) => i.amount > 0);
        }
        return updated;
      });
  
      // Mark “capturing” and set initial frame = 0 (closed ball)
      setWildPokemon((wp) =>
        wp.map((p) =>
          p.id === poke.id ? { ...p, capturing: true, captureFrame: 0, originalSprite: p.sprite, usedBallItemId: ball.item } : p
        )
      );
  
      if (res.data.success) {
        //
        // ─── SUCCESS ───
        //
        // Simply mark the Pokemon as captured without any special animations
        setWildPokemon((wp) =>
          wp.map((p) =>
            p.id === poke.id
              ? { ...p, capturing: true, captureFrame: 0 }
              : p
          )
        );
        
        setTimeout(() => {
          setWildPokemon((wp) => wp.filter((p) => p.id !== poke.id));
  
          // Always use the position from the ref to ensure we're using the saved position
          const pos = capturePositionRef.current || capturePosition;
          spawnText(`+1 ${poke.species}`, pos.x, pos.y - 16, "green");
          const exp = res.data.exp_gained;
          const formatExp = exp.toLocaleString();
          setTimeout(() =>
            spawnText(`+${formatExp} XP`, pos.x, pos.y - 32, "green")
          , 250);
          
          // We'll adjust the positioning of the anti-flee scent message based on whether there are other messages
          let scentMessageDelay = 500;
          let scentMessageYOffset = -48;
          
          // If there's a reward item, we need to position the scent message lower
          if (res.data.reward_item) {
            scentMessageYOffset = -80; // Position below reward item message
            scentMessageDelay = 750; // Show after reward item message
            
            // If there's also a Pokedex entry, position even lower
            if (res.data.pokedex_entry) {
              scentMessageYOffset = -96; // Position below Pokedex entry message
              scentMessageDelay = 1000; // Show after Pokedex entry message
            }
          } else if (res.data.pokedex_entry) {
            // If there's only a Pokedex entry but no reward item
            scentMessageYOffset = -64; // Position below Pokedex entry message
            scentMessageDelay = 750; // Show after Pokedex entry message
          }
          
          // Check if scent_remaining is in the response and display it
          if (res.data.scent_remaining && res.data.scent_remaining > 0) {
            setTimeout(() =>
              spawnText(`Anti-flee Scent: ${res.data.scent_remaining} uses left`, pos.x, pos.y + scentMessageYOffset, "#9370DB")
            , scentMessageDelay);
          }
          
          // Handle special ball rewards
          if (res.data.reward_item) {
            // Get the appropriate image for the reward item
            let rewardImage;
            switch(res.data.reward_item) {
              case "rock": rewardImage = rockImg; break;
              case "ruby": rewardImage = rubyImg; break;
              case "sapphire": rewardImage = sapphireImg; break;
              case "emerald": rewardImage = emeraldImg; break;
              case "gold": rewardImage = goldImg; break;
              case "diamond": rewardImage = diamondImg; break;
              default: rewardImage = null;
            }
            
            // Format the reward item name with proper capitalization
            const rewardName = res.data.reward_item.charAt(0).toUpperCase() + res.data.reward_item.slice(1);
            
            // Display the reward with the image
            setTimeout(() => {
              spawnText(`+1 ${rewardName}`, pos.x, pos.y - 48, "#ffe066", rewardImage);
            }, 500);
            
            // If there's also a Pokedex entry, show it after the reward
            if (res.data.pokedex_entry) {
              setTimeout(() => {
                spawnText(`New Pokédex Entry!`, pos.x, pos.y - 64, "red");
              }, 750);
            }
          } else if (res.data.pokedex_entry) {
            // If no reward but there is a Pokedex entry, show it at the normal position
            setTimeout(() => {
              spawnText(`New Pokédex Entry!`, pos.x, pos.y - 48, "red");
            }, 500);
          }
        }, 4000);
      } else {
        //
        // ─── FAILURE (possibly fled) ───
        //
        // Play the 3‐frame "open‐and‐shake" animation (frames 1→2→3 at 1s intervals).
        //
        [1, 2, 3].forEach((frame, i) =>
          setTimeout(() => {
            setWildPokemon((wp) =>
              wp.map((p) =>
                p.id === poke.id ? { ...p, captureFrame: frame, usedBallItemId: selectedBall?.item } : p
              )
            );
          }, 1000 * (i + 1))
        );
  
        setTimeout(() => {
          if (res.data.fled) {
            // After the 3‐frame shake, remove Pokémon & show "Pokémon fled!"
            const pos = capturePositionRef.current || capturePosition;
            spawnText("Pokémon fled!", pos.x, pos.y - 16, "red");
            setWildPokemon((wp) => wp.filter((p) => p.id !== poke.id));
          } else {
            // Show a red burst animation when Pokemon breaks out of ball
            // Create the breakout animation effect with ball-specific styling
            const breakoutEffectId = Date.now() + Math.random();
            setCaptureEffects(prev => [...prev, {
              id: breakoutEffectId,
              type: 'breakout',
              x: poke.x,
              y: poke.y,
              ballType: getBallClass(selectedBall?.item),
              created: Date.now()
            }]);
            
            // After a delay, clear the effect and show the Pokemon
            setTimeout(() => {
              // First clean up the burst effect
              setCaptureEffects(effects => effects.filter(e => e.id !== breakoutEffectId));
              
              // Then restore the Pokemon with its original sprite
              setWildPokemon((wp) =>
                wp.map((p) =>
                  p.id === poke.id
                    ? {
                        ...p,
                        capturing: false,
                        captureFrame: 0,
                        sprite: p.originalSprite,
                        usedBallItemId: selectedBall?.item
                      }
                    : p
                )
              );
              
              // Display remaining anti-flee scent attempts if available
              if (res.data.scent_active && res.data.scent_remaining > 0) {
                const pos = capturePositionRef.current || capturePosition;
                setTimeout(() => {
                  // Position the message below the "Capture failed" message
                  spawnText(`Anti-flee Scent: ${res.data.scent_remaining} uses left`, pos.x, pos.y - 32, "#9370DB");
                }, 300);
              }
            }, 600);
          }
        }, 4000);
      }
    } catch (err) {
      console.error(err);
      // Only show error for actual API errors, not for player movement
      
      // Check if the error is a server/network error (not just player movement)
      // We only want to show errors for actual API/network issues
      if (err.response && err.response.status >= 400) {
        // Use the saved position from capturePositionRef to ensure consistent positioning
        const pos = capturePositionRef.current || capturePosition;
        spawnText("Error during capture!", pos.x, pos.y - 16, "red");
      }
      
      // On any error, restore pokemon immediately
      setWildPokemon((wp) =>
        wp.map((p) =>
          p.id === poke.id
            ? { ...p, capturing: false, captureFrame: 0, sprite: p.originalSprite, usedBallItemId: selectedBall?.item }
            : p
        )
      );
      
      // Clean up any effects
      setCaptureEffects(effects => effects.filter(e => 
        e.x !== poke.x || e.y !== poke.y
      ));
    }
  }
  

  
  

  // —— Floating text —— 
  const spawnText = (text, x, y, color = "#ffe066", imageUrl = null) => {
    const id = Date.now() + Math.random();
    setFloatingTexts((ft) => [
      ...ft,
      { id, text, x, y, color, imageUrl, created: Date.now() },
    ]);
    setTimeout(() => {
      setFloatingTexts((ft) => ft.filter((f) => f.id !== id));
    }, 2000);
  };


  const resetRocks = async () => {
    try {
      // Clear the current rocks
      setRocks([]);


      // Fetch new rocks from the server using the correct endpoint format
      const response = await api.get(`/v1/rocks`, { 
        params: { map_id: MAP_ID },
        withCredentials: true 
      });

      // Update rocks with the new data
      setRocks(response.data.data || []);

      // Show success message
      spawnText("Rocks refreshed!", playerPos.x, playerPos.y+32, "#00ff00");
    } catch (error) {
      console.error("Error refreshing rocks:", error);
      spawnText("Failed to refresh rocks", playerPos.x, playerPos.y - 16, "#ff0000");
    }
  };

  // —— Rock breaking —— 
  const handleRockClick = (rock) => {
    // 1) If this rock is already "breaking", do nothing
    if (breakingFrames[rock.id] != null) return;
    
    // Save the player position at the start of mining
    const miningPosition = { ...playerPos }; // Clone to avoid reference issues
    // Store the mining position in a ref to ensure we have access to it throughout the animation
    // This is critical for making mining work with analog stick movement
    miningPositionRef.current = miningPosition;
    
    // Check if this is an Item Finder item
    const isItemFinderItem = rock.item_type && rock.item_type.startsWith('item_tier');
    
    // If it's an Item Finder item, use a different animation and collection logic
    if (isItemFinderItem) {
      
      // Show a sparkle animation instead of breaking animation
      setBreaking(b => ({
        ...b,
        [rock.id]: 0
      }));
      
      // Animate a quick sparkle effect
      [1, 2, 3].forEach((frame, i) => {
        setTimeout(() => {
          setBreaking(b => ({
            ...b,
            [rock.id]: frame
          }));
        }, 150 * (i + 1)); // Faster animation for items
      });
      
      // After the sparkle animation, send the collect request
      setTimeout(async () => {
        try {
          const res = await api.post(
            "/v1/rocks/collect",
            { map_id: MAP_ID, rock_id: rock.id },
            { withCredentials: true }
          );
          
          if (res.data.success) {
            // Remove from map
            setRocks(r => r.filter(rk => rk.id !== rock.id));
            
            // Get item details
            const drop = res.data.dropItem;
            const pretty = drop.charAt(0).toUpperCase() + drop.slice(1).replace(/_/g, ' ');
            
            // Determine item color and message based on the item_type
            let color = "#ffe066";
            let message = "";
            
            if (rock.item_type.includes('tier1')) {
              color = "#a0e6ff"; // Light blue for common items
              message = `You found a ${pretty}!`;
            } else if (rock.item_type.includes('tier2')) {
              color = "#50c878"; // Green for uncommon items
              message = `You found an ${pretty}!`;
            } else if (rock.item_type.includes('tier3')) {
              color = "#ffd700"; // Gold for rare items
              message = `You found a ${pretty}!`;
            }
            
            // Get the appropriate icon URL
            const itemIconPath = `/Maps/items/${drop}.png`;
            
            // Show a special notification for Item Finder discoveries
            spawnText(
              message,
              miningPositionRef.current.x,
              miningPositionRef.current.y - 16,
              color,
              itemIconPath
            );
            
            // Show XP gained
            setTimeout(() => {
              spawnText(
                `+${res.data.expGained} Mining XP`,
                miningPositionRef.current.x,
                miningPositionRef.current.y - 32,
                color
              );
            }, 400);
            
            // No need to update pickaxe durability for Item Finder items
            // But still update lure hits
            setExpLureHits(h => Math.max(0, h - 1));
            setDropLureHits(h => Math.max(0, h - 1));
          }
        } catch (e) {
          // Handle error
          const pos = miningPositionRef.current || miningPosition;
          spawnText(
            `Error collecting item.`,
            pos.x,
            pos.y - 16,
            "red"
          );
        } finally {
          // Clear the breaking/sparkle flag
          setBreaking(b => {
            const { [rock.id]: _, ...rest } = b;
            return rest;
          });
        }
      }, 500); // Shorter timeout for item collection
    } else {
      // Original rock breaking logic for normal rocks
      // 2) Lock immediately (frame 0 = "just started")
      setBreaking(b => ({
        ...b,
        [rock.id]: 0
      }));
    
      // 3) Animate frames 1→2→3 at 250 ms intervals
      [1, 2, 3].forEach((frame, i) => {
        setTimeout(() => {
          setBreaking(b => ({
            ...b,
            [rock.id]: frame
          }));
        }, 250 * (i + 1));
      });
    
      // 4) After the "smash" animation, send the collect request
      setTimeout(async () => {
        try {
          const res = await api.post(
            "/v1/rocks/collect",
            { map_id: MAP_ID, rock_id: rock.id },
            { withCredentials: true }
          );
          if (res.data.success) {
            // remove from map
            setRocks(r => r.filter(rk => rk.id !== rock.id));
    
            // spawn loot text…
            const drop = res.data.dropItem;
            
            // Map stone IDs to proper names
            const stoneNameMap = {
              "stone_112": "Fire Stone",
              "stone_34": "Water Stone",
              "stone_114": "Thunder Stone",
              "stone_126": "Leaf Stone",
              "stone_22": "Moon Stone",
              "stone_26": "Dawn Stone",
              "stone_151": "Metal Coat",
              "stone_135": "Kings Rock"
            };
            
            // Use stone name map if it's a stone, otherwise format normally
            const pretty = stoneNameMap[drop] || (
              drop.includes('_') ? 
                drop.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 
                drop.charAt(0).toUpperCase() + drop.slice(1)
            );
            
            const iconUrl = rockIcons[drop] || rockIcons.rock;
            const colorMap = {
              rock: "#999", ruby: "#e0115f", emerald: "#50c878",
              sapphire: "#0f52ba", gold: "#ffd700", diamond: "#a0e6ff",
              fire_stone: "#ff5733", water_stone: "#33a0ff", thunder_stone: "#ffcc00",
              // Colors for stone items
              stone_112: "#ff5733", // Fire Stone - orange-red
              stone_34: "#33a0ff",  // Water Stone - blue
              stone_114: "#ffcc00", // Thunder Stone - yellow
              stone_126: "#777777", // Lead Stone - gray
              stone_22: "#e6e6fa",  // Moon Stone - light purple
              stone_26: "#ffc0cb",  // Dawn Stone - pink
              stone_151: "#c0c0c0", // Metal Coat - silver
              stone_135: "#daa520"  // Kings Rock - goldenrod
            };
            const color = colorMap[drop] || "#ffe066";
    
            // Always use miningPositionRef.current for consistent floating text position
            spawnText(`+1 ${pretty}`, miningPositionRef.current.x, miningPositionRef.current.y - 16, color, iconUrl);
    
            setTimeout(() =>
              spawnText(`+${res.data.expGained} Mining XP`, miningPositionRef.current.x, miningPositionRef.current.y - 32, color),
            400);
    
            // update pickaxe durability, lure hits, etc…
            setEquippedPickaxe(ep => {
              if (!ep) return null;
              const nd = ep.durability - 1;
              return nd > 0 ? { ...ep, durability: nd } : null;
            });
            setExpLureHits(h => Math.max(0, h - 1));
            setDropLureHits(h => Math.max(0, h - 1));
          }
        } catch (e) {
          // Always use the position from the ref to ensure we're using the saved position
          const pos = miningPositionRef.current || miningPosition;
          spawnText(
            `You must equip a pickaxe before mining.`,
            pos.x,
            pos.y - 16,
            "red"
          );
        } finally {
          // 5) Unlock / clear the breaking flag
          setBreaking(b => {
            const { [rock.id]: _, ...rest } = b;
            return rest;
          });
        }
      }, 750);
    }
  };

  // —— Berry Plant Handlers ——
  const handlePlantClick = async (slotIndex) => {
   
    // Check if player has selected a berry
    if (!selectedAction || selectedAction.type !== "berry") {
      spawnText("Select a berry from your pouch first!", playerPos.x, playerPos.y - 16, "#ff0000");
      return;
    }
    
    const berryId = selectedAction.payload.item;
    
    
    // Check if player has the selected berry in inventory
    const selectedBerry = inventory.find(item => item.item === berryId);
    
    if (!selectedBerry || selectedBerry.amount < 1) {
      spawnText("You don't have any of that berry!", playerPos.x, playerPos.y - 16, "#ff0000");
      return;
    }
    

    try {
      const response = await api.post(
        "/v1/berry-plants/plant",
        { slot_index: slotIndex, berry_id: berryId },
        { withCredentials: true }
      );

      if (response.data.success) {
        spawnText(`Planted ${response.data.berry_name}!`, response.data.position.x, response.data.position.y - 16, "#00ff00");
        
        // Immediately deduct berry from inventory (optimistic update)
        setInventory((inv) => {
          const updated = inv.map((i) =>
            i.item === berryId ? { ...i, amount: i.amount - 1 } : i
          ).filter(i => i.amount > 0); // Remove if amount reaches 0
          
          // Clear selection if we just used the last berry
          const berryStillExists = updated.find(i => i.item === berryId);
          if (!berryStillExists) {
            setSelectedAction(null);
            localStorage.removeItem("selectedBerry");
          }
          
          return updated;
        });
        
        refreshBerryPlants();
      }
    } catch (error) {
      console.error("Error planting berry:", error);
      const errorMsg = error.response?.data?.detail || "Failed to plant berry";
      spawnText(errorMsg, playerPos.x, playerPos.y - 16, "#ff0000");
      
      // Reload inventory on error to sync with server
      api.get("/v1/inventory", { withCredentials: true })
        .then((r) => {
          const filtered = r.data.filter((i) =>
            [2, 3, 4, 199,200, 234,167, 235, 116, 117, 6, 7, 8, 9, 10, 4000, 4001, 4002, 4003, 4004, 4005, 4006, 4007,4008, 214, 701780, 701781].includes(i.item) &&
            i.amount > 0
          );
          setInventory(filtered);
        }).catch(console.error);
    }
  };

  const handleHarvestClick = async (plant) => {
    if (!plant.is_ready) {
      const remainingPercent = Math.round(100 - plant.growth_percent);
      spawnText(`Not ready yet! ${remainingPercent}% to go`, plant.x, plant.y - 16, "#ffaa00");
      return;
    }

    try {
      const response = await api.post(
        "/v1/berry-plants/harvest",
        { slot_index: plant.slot_index },
        { withCredentials: true }
      );

      if (response.data.success) {
        spawnText(
          `Harvested ${response.data.harvest_amount} ${response.data.berry_name}!`,
          plant.x,
          plant.y - 16,
          "#ffd700"
        );
        
        // Display farming EXP gained
        if (response.data.farming_exp) {
          setTimeout(() => {
            spawnText(
              `+${response.data.farming_exp} Farming EXP`,
              plant.x,
              plant.y - 32,
              "#10b981"
            );
          }, 250);
        }
        
        refreshBerryPlants();
        // Reload inventory to reflect harvested berries
        api.get("/v1/inventory", { withCredentials: true })
          .then((r) => {
            const filtered = r.data.filter((i) =>
              [2, 3, 4, 199,200,201,202,203, 234,167, 235, 116, 117, 6, 7, 8, 9, 10, 4000, 4001, 4002, 4003, 4004, 4005, 4006, 4007,4008, 214, 701780, 701781].includes(i.item) &&
              i.amount > 0
            );
            setInventory(filtered);
          }).catch(console.error);
      }
    } catch (error) {
      console.error("Error harvesting berry:", error);
      const errorMsg = error.response?.data?.detail || "Failed to harvest berry";
      spawnText(errorMsg, plant.x, plant.y - 16, "#ff0000");
    }
  };

  

  const computeShinyOdds = (copies = 0) => {
    const denom = Math.max(1, 4096 / Math.pow(2, copies));
    return (1 / denom) * 100;
  };

  // —— Use lure —— 
  const handleLureClick = useCallback(async (itemId) => {
    try {
      const res = await api.post(
        "/v1/use_item",
        { item_id: itemId },
        { withCredentials: true }
      );
      if (res.data.success) {
        setInventory((inv) =>
          inv
            .map((i) =>
              i.item === itemId ? { ...i, amount: i.amount - 1 } : i
            )
            .filter((i) => i.amount > 0)
        );
        spawnText(res.data.message, playerPos.x, playerPos.y - 16, "#fff");
        if ([214, 701780, 701781].includes(itemId)) {
          if (itemId === 214) setActiveLure("lure");
          else if (itemId === 701780) setActiveLure("super_lure");
          else setActiveLure("ultra_lure");
          setLureExpire(res.data.expiration);
        } else if (itemId === 234) {
          setExpLureHits((h) => h + 50);
          spawnText(
            "+50 Mining XP hits!",
            playerPos.x,
            playerPos.y - 32,
            "#ffe066",
            mineLureImg
          );
        } else if (itemId === 235) {
          setDropLureHits((h) => h + 50);
          spawnText(
            "+50 Drop‐rate hits!",
            playerPos.x,
            playerPos.y - 32,
            "#ffe066",
            dropLureImg
          );
        }
      } else {
        spawnText(
          res.data.error || "Failed to use lure",
          playerPos.x,
          playerPos.y - 16,
          "red"
        );
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
    const msg = detail || "Failed to use lure";
    console.error("use_item error:", err);
    spawnText(msg, playerPos.x, playerPos.y - 16, "red");
    }
  }, [api, playerPos, setInventory, spawnText, setActiveLure, setLureExpire, setExpLureHits, setDropLureHits]);

// 1️⃣ wrap handleLureClick so it auto‐fires, closes pouch & clears selection
const firePotion = React.useCallback(async (itemId) => {
  await handleLureClick(itemId);
  setShowPouch(false);
  setSelectedAction(null);
}, [handleLureClick]);

// 2️⃣ this is the only setter you hand to InventryPane
const handleSetSelectedAction = React.useCallback(action => {
  if (action.type === 'item' && [234, 235,214,6,7,8,9,10, 701780, 701781].includes(action.payload)) {
    // potions fire immediately
    firePotion(action.payload);
  }
  else if (action.type === 'ball') {
    // select ball in state
    setSelectedBall(action.payload);
    setSelectedAction(action);
    // persist selection
    localStorage.setItem(
      'selectedBall',
      action.payload.item.toString()
    );
  }
  else {
    // rocks or true lures
    setSelectedAction(action);
  }
}, [firePotion]);




  // —— WebSocket & reconnection logic —— 
  useEffect(() => {
    let retryDelay = 2000; // start with 2s retry
    function connectWebSocket() {
      const proto = window.location.protocol === "https:" ? "wss" : "ws";
      const host =
        window.location.hostname === "localhost" ? "localhost:8000" : "pokemonradiance.com";
      ws.current = new WebSocket(`${proto}://${host}/ws/pokemon`);

      ws.current.onopen = () => {
        retryDelay = 2000; // reset delay on successful open
        const token = localStorage.getItem("jwtToken");
        if (ws.current.readyState === 1) {
          ws.current.send(JSON.stringify({ map_id: MAP_ID, token: token }));
        }
      };

      ws.current.onmessage = ({ data }) => {
        const d = JSON.parse(data);
        
        // Check for error messages
        if (d.error) {
          return;
        }
        
        // Handle portal information
        if (d.portals) {
          setPortals(d.portals);
        } else {
          setPortals([]);
        }
        
        // Check if pokemon array exists before filtering
        if (d.pokemon) {
          // Only show non‐captured Pokémon, but preserve existing step values for animation
          // Also preserve fishing Pokemon (client-side spawned)
          setWildPokemon(prev => {
            // Keep fishing Pokemon (those with IDs starting with 'fishing_')
            const fishingPokemon = prev.filter(p => p.id && p.id.toString().startsWith('fishing_'));
            
            const newPokemon = d.pokemon.filter((p) => !p.captured);
            const mappedPokemon = newPokemon.map(newPoke => {
              const existing = prev.find(p => p.id === newPoke.id);
              // Ensure step is always a valid number (0-3), defaulting to 0 if invalid
              const step = (existing && typeof existing.step === 'number' && !isNaN(existing.step)) 
                ? existing.step 
                : (typeof newPoke.step === 'number' && !isNaN(newPoke.step) ? newPoke.step : 0);
              return { ...newPoke, step };
            });
            
            // Combine server Pokemon with fishing Pokemon
            return [...mappedPokemon, ...fishingPokemon];
          });
        } else {
          // Handle case where pokemon array is missing - keep fishing Pokemon
          console.warn("WebSocket response missing pokemon array:", d);
          setWildPokemon(prev => prev.filter(p => p.id && p.id.toString().startsWith('fishing_')));
        }
        
        // Handle day/night cycle information
        if (d.isNight !== undefined) {
          setIsNight(d.isNight);
        }
        
        // Only set expiration timer if expiresAt exists and is in the future
        if (d.expiresAt) {
          const ms = new Date(d.expiresAt) - new Date();
          clearTimeout(expireTO.current);
          
          // Only set timer if expiration is more than 5 seconds in the future
          // This prevents immediate re-requests and conflicts with backend timing
          if (ms > 5000) {
            expireTO.current = setTimeout(() => {
              if (ws.current?.readyState === 1) {
                ws.current.send(
                  JSON.stringify({
                    map_id: MAP_ID,
                    token: localStorage.getItem("jwtToken"),
                  })
                );
              }
            }, ms);
          }
        }
      };

      ws.current.onerror = (e) => {
        console.error("WebSocket error:", e);
      };

      ws.current.onclose = () => {
        console.log("WS closed, attempting reconnect in", retryDelay, "ms");
        clearTimeout(expireTO.current);
        reconnectTO.current = setTimeout(() => {
          connectWebSocket();
          retryDelay = Math.min(retryDelay * 1.5, 20000);
        }, retryDelay);
      };
    }
    connectWebSocket();
    return () => {
      clearTimeout(expireTO.current);
      clearTimeout(reconnectTO.current);
      if (ws.current) {
        ws.current.onopen = null;
        ws.current.onmessage = null;
        ws.current.onerror = null;
        ws.current.onclose = null;
        ws.current.close();
      }
    };
  }, [MAP_ID]);

  
  if (inBattle && initialBattleData) {
    return (
      <BattleScreen
      initialData={initialBattleData}
      onExit={() => { setInBattle(false); setInitialBattleData(null); }}
      onBattleEnd={handleBattleEnd}
      battleBgUrl={config.battleBackground || "/Battle/green.png"}
    />
    );
  }

  // Handle portal click - simplified version that always teleports
  const handlePortalClick = (portal) => {
    // Show teleport message
    spawnText(
      "Entering Halloween Event...",
      playerPos.x,
      playerPos.y - 16,
      "#9400D3" // Purple color for Halloween theme
    );
    
    // Navigate to Halloween event map after a short delay
    setTimeout(() => {
      navigate("/map/HalloweenEvent");
    }, 1000);
  };

  const handleTrainerClick = async (trainer) => {
 
  try {
    const r = await api.get("/v1/roster", { withCredentials: true });
    const anyAlive = r.data.some((p) => p.current_hp > 0);
    if (!anyAlive) {
      spawnText(
        "All your Pokémon are fainted! You can't battle trainers.",
        playerPos.x,
        playerPos.y - 16,
        "red"
      );
      return;
    }
  } catch (err) {
    console.error("Failed to fetch roster:", err);
    spawnText(
      "Could not check your roster. Try again later.",
      playerPos.x,
      playerPos.y - 16,
      "red"
    );
    return;
  }

  // 2️⃣ Then start the trainer battle as before
  try {
    const res = await api.post(
      "/v1/battle/start",
      {
        opponent_type: "trainer",
        trainer_id: trainer.trainer_id,
      },
      { withCredentials: true }
    );
setInitialBattleData({
       ...res.data,
       opponent_type: "trainer",
      trainer_name: trainer.trainer_name
     });    setInBattle(true);
  } catch (err) {
    console.error("Failed to start trainer battle:", err);
    const msg =
      err.response?.data?.detail ||
      err.response?.data?.message ||
      "Can't battle right now!";
    spawnText(msg, playerPos.x, playerPos.y - 16, "red");
  }
};


  // —— Render —— 
  
  return (
    <div className="layout-container">
      <style>{`
          .floating-text {
            position: absolute;
            pointer-events: none;
            font: bold 12px sans-serif;
            animation: float-and-fade 6s ease-out forwards;
            text-shadow: 1px 1px 2px rgba(0,0,0,0.6);
            z-index: 10;
          }
          @keyframes float-and-fade {
            0% { transform: translateY(0); opacity: 1; }
            100% { transform: translateY(-30px); opacity: 0; }
          }
          .health-bar {
            width: 100%;
            height: 6px;
            background-color: #333;
            border-radius: 3px;
            overflow: hidden;
            margin-top: 4px;
            box-shadow: 0 0 2px rgba(0,0,0,0.5) inset;
          }
          .health-bar-fill {
            height: 100%;
            border-radius: 3px;
            transition: width 0.3s ease;
            box-shadow: 0 0 3px rgba(255,255,255,0.3);
          }
          .health-bar-pickaxe {
            background-color: #f0ad4e;
            background-image: linear-gradient(to bottom, #f0ad4e, #c77c19);
          }
          .health-bar-exp {
            background-color: #5bc0de;
            background-image: linear-gradient(to bottom, #5bc0de, #2390b0);
          }
          .health-bar-drop {
            background-color: #5cb85c;
            background-image: linear-gradient(to bottom, #5cb85c, #3d8b3d);
          }
          
          @keyframes float-and-fade {
            0% { transform: translateY(0); opacity: 1; }
            100% { transform: translateY(-30px); opacity: 0; }
          }
          
          .health-bar {
            width: 100%;
            height: 6px;
            background-color: #333;
            border-radius: 3px;
            overflow: hidden;
            margin-top: 4px;
            box-shadow: 0 0 2px rgba(0,0,0,0.5) inset;
          }
          .health-bar-fill {
            height: 100%;
            border-radius: 3px;
            transition: width 0.3s ease;
            box-shadow: 0 0 3px rgba(255,255,255,0.3);
          }
          .health-bar-pickaxe {
            background-color: #f0ad4e;
            background-image: linear-gradient(to bottom, #f0ad4e, #c77c19);
          }
          .health-bar-exp {
            background-color: #5bc0de;
            background-image: linear-gradient(to bottom, #5bc0de, #2390b0);
          }
          .health-bar-drop {
            background-color: #5cb85c;
            background-image: linear-gradient(to bottom, #5cb85c, #3d8b3d);
          }
        `}</style>

      <div className="map-section">
        <div className="map-wrapper">
          <div
            className="map-scale-wrapper"
            style={{
              transform: `scale(${mapScale})`,
              transformOrigin: "top center",
            }}
          >
            <div className="game-container">
              {/* Pouch icon */}
              <div
                onClick={togglePouch}
                style={{
                  position: "absolute",
                  bottom: 1,
                  right: 1,
                  width: 36,
                  height: 36,
                  background: "rgba(0,0,0,0.5)",
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  zIndex: 1000,
                }}
              >
                <img
                  src={pouchIcon}
                  alt="Pouch"
                  style={{ width: 24, height: 24 }}
                />
              </div>

              {/* Pouch overlay with inventory + gems */}
              {showPouch && (
                <div
                  className="pouch-overlay"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background: "rgba(0,0,0,0.7)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1001,
                  }}
                  onClick={togglePouch}
                >
                 <InventoryPanel
          inventory={inventory}
          gems={gems}
          selectedAction={selectedAction}
          // ← swap out the raw setter for our wrapped one
          setSelectedAction={setSelectedAction}
          handleLureClick={handleLureClick}
          resetRocks={resetRocks}
          spawnText={spawnText}
          playerPos={playerPos}
          onMineralPotionUse={handleMineralPotionUse}
          activeLure={activeLure}
        />
                </div>
              )}
{/* Lure hits display – only if there's at least one hit left */}
{(expLureHits > 0 || dropLureHits > 0) && (
  <div
    style={{
      position: "absolute",
      bottom: 8,
      left: 8,
      zIndex: 1000,
      background: "rgba(0,0,0,0.5)",
      padding: "4px 8px",
      borderRadius: "4px",
      color: "#fff",
      fontSize: "0.75rem",
      textAlign: "left",
      pointerEvents: "none",
      minWidth: "40px",
    }}
  >
    {expLureHits > 0 && (
      <div style={{ marginBottom: "4px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <img src={mineLureImg} alt="Mining Lure" style={{ width: 20, height: 20 }} />
        </div>
        <div className="health-bar">
          <div 
            className="health-bar-fill health-bar-exp" 
            style={{ 
              width: `${Math.min((expLureHits / 50) * 100, 100)}%` 
            }}
          />
        </div>
      </div>
    )}
    {dropLureHits > 0 && (
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <img src={dropLureImg} alt="Drop Lure" style={{ width: 20, height: 20 }} />
        </div>
        <div className="health-bar">
          <div 
            className="health-bar-fill health-bar-drop" 
            style={{ 
              width: `${Math.min((dropLureHits / 50) * 100, 100)}%` 
            }}
          />
        </div>
      </div>
    )}
  </div>
)}


         

              {/* Equipped Pickaxe or Item Finder display */}
              {(equippedPickaxe || equippedItemFinder) && (
                <div
                  style={{
                    position: "absolute",
                    top: 4,
                    left: 4,
                    background: "rgba(0,0,0,0.6)",
                    padding: "4px 6px",
                    borderRadius: "3px",
                    textAlign: "center",
                    zIndex: 999,
                    minWidth: "40px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {equippedItemFinder ? (
                      <img
                        src={`/${equippedItemFinder.item_image}`}
                        alt={equippedItemFinder.item_name}
                        style={{ width: 24, height: 24 }}
                      />
                    ) : (
                      <img
                        src={`/${equippedPickaxe.item_image}`}
                        alt={equippedPickaxe.item_name}
                        style={{ width: 24, height: 24 }}
                      />
                    )}
                  </div>
                  {equippedPickaxe && !equippedItemFinder && (
                    <div className="health-bar">
                      <div 
                        className="health-bar-fill health-bar-pickaxe" 
                        style={{ 
                          width: `${(equippedPickaxe.durability / pickaxeMaxDurability[equippedPickaxe.item]) * 100}%` 
                        }}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Equipped Fishing Rod display */}
              {equippedRod && (
                <div
                  style={{
                    position: "absolute",
                    top: 4,
                 
                    background: "rgba(0,0,0,0.6)",
                    padding: "4px 6px",
                    borderRadius: "3px",
                    textAlign: "center",
                    zIndex: 999,
                    minWidth: "40px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <img
                      src={`/${equippedRod.item_image}`}
                      alt={equippedRod.item_name}
                      style={{ width: 24, height: 24 }}
                    />
                  </div>
                  <div className="health-bar">
                    <div 
                      className="health-bar-fill health-bar-pickaxe" 
                      style={{ 
                        width: `${(equippedRod.durability / 50) * 100}%` 
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Shiny Charm display */}
              {shinyCharm && shinyCharm.stack_amount > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    background: "rgba(0,0,0,0.6)",
                    padding: "2px 4px",
                    borderRadius: "3px",
                    textAlign: "center",
                    zIndex: 999,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      marginRight: "4px",
                      fontSize: "0.75rem",
                      color: "#fff",
                    }}
                  >
                    Shiny odds:{" "}
                    {(shinyCharm && shinyCharm.stack_amount > 0) ? 
                      `${computeShinyOdds(shinyCharm.stack_amount).toFixed(2)}% (${shinyCharm.stack_amount}×)` : 
                      "0.024% (0×)"}
                  </span>
                  <img
                    src={`/${shinyCharm.item_image}`}
                    alt={shinyCharm.item_name}
                    style={{ width: 20, height: 20 }}
                  />
                </div>
              )}
              
              {/* Catching Gloves indicator */}
              {equippedCatchingGloves && equippedCatchingGloves.stack_amount > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: shinyCharm && shinyCharm.stack_amount > 0 ? 34 : 4,
                    right: 4,
                    width: "32px",
                    height: "32px",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 10,
                  }}
                >
                  <img
                    src="/items/catchinggloves.png"
                    alt="Catching Gloves"
                    style={{ width: "24px", height: "24px" }}
                  />
                </div>
              )}
              
              {/* Ring of Department indicator */}
              {hasDepartmentRing && (
                <button
                  style={{
                    position: "absolute",
                    top: (shinyCharm && shinyCharm.stack_amount > 0 ? 34 : 4) + 
                         (equippedCatchingGloves && equippedCatchingGloves.stack_amount > 0 ? 34 : 0) + 
                         (hasEquippedRing ? 34 : 0), // Position below Ring of Equipment
                    right: 4,
                    width: "32px",
                    height: "32px",
                    backgroundColor: "rgba(255, 215, 0, 0.3)",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 998, // Lower z-index than Ring of Equipment
                    border: "1px solid #ffd700",
                    cursor: "pointer",
                    padding: 0,
                    outline: "none",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    transition: "all 0.2s ease",
                  }}
                  onClick={() => {
                    console.log('Ring of Department indicator clicked');
                    // Create a market emoji floating text at player position
                    const id = Date.now() + Math.random();
                    setFloatingTexts((ft) => [
                      ...ft,
                      { id, text: '🛒', x: playerPos.x, y: playerPos.y - 20, color: '#ffd700', created: Date.now() },
                    ]);
                    
                    // Open the Department Store modal
                    setDepartmentStoreModalOpen(true);
                    
                    // Fetch store items and currency data
                    Promise.all([
                      api.get('/v1/store', { withCredentials: true }),
                      api.get('/v1/currency', { withCredentials: true }),
                      api.get('/v1/catchrate', { withCredentials: true })
                    ])
                      .then(([storeRes, currencyRes, catchRateRes]) => {
                        // Filter store items to only include department store items
                        const departmentStoreItems = (storeRes.data || []).filter(item => 
                          // Pokeballs
                          [2, 3, 4, 4000, 4001, 4002, 4003, 4004, 4005, 4006, 4007].includes(item.id) ||
                          // Map items (lures, charms)
                          [214, 701780, 701781, 194, 195].includes(item.id)
                        );
                        
                        setStoreItems(departmentStoreItems);
                        
                        // Handle currency data
                        if (Array.isArray(currencyRes.data)) {
                          if (currencyRes.data.length) setCurrency(currencyRes.data[0]);
                        } else {
                          setCurrency(currencyRes.data);
                        }
                      })
                      .catch(error => {
                        console.error('Error fetching data:', error);
                      });
                    
                    setTimeout(() => {
                      setFloatingTexts((ft) => ft.filter((f) => f.id !== id));
                    }, 2500);
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "rgba(255, 215, 0, 0.5)";
                    e.target.style.boxShadow = "0 4px 8px rgba(0,0,0,0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "rgba(255, 215, 0, 0.3)";
                    e.target.style.boxShadow = "0 2px 4px rgba(0,0,0,0.2)";
                  }}
                  title="Open Department Store"
                >
                  <span style={{ fontSize: "20px" }}>🛒</span>
                </button>
              )}

              {/* Held Item Quick Swap Button - only shown when ring is equipped */}
              {hasEquippedRing && (
                <div
                  onClick={() => {
          
                    setShowHeldItemSwap(true);
                  }}
                  style={{
                    position: "absolute",
                    top: shinyCharm && shinyCharm.stack_amount > 0 ? 34 : 4,
                    right: 4,
                    width: "32px",
                    height: "32px",
                    background: "rgba(0,0,0,0.6)",
                    border: "none",
                    borderRadius: "8px",
                    color: "#ffffff",
                    fontSize: "16px",
                    cursor: "pointer",
                    zIndex: 999, // Higher z-index than Ring of Department
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "none",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "rgba(0,0,0,0.8)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "rgba(0,0,0,0.6)";
                  }}
                  title="Quick Held Item Swap"
                >
                  ⚒️
                </div>
              )}

              {/* Map overlay */}
              
              <div
                className="map-overlay"
                style={{
                  backgroundImage: `url(${overImageUrl})`,
                  backgroundPosition: `-${camX}px -${camY}px`,
                }}
              />

              {/* Map base */}
              <div
                className="map-visibility"
                style={{
                  backgroundImage: `url(${mapImageUrl})`,
                  backgroundPosition: `-${camX}px -${camY}px`,
                }}
              />
              
              {/* Night effect overlay - only shown during nighttime */}
              {isNight && (
                <div
                  className="night-overlay"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 40, 0.5)',
                    pointerEvents: 'none',
                    zIndex: 5
                  }}
                />
              )}
              {collisionAreas.map((area, idx) => {
            const screenX = area.x - camX;
            const screenY = area.y - camY;
            return (
              <div
                key={idx}
                className="collision-overlay"
                style={{
                  left: screenX,
                  top: screenY,
                  width: area.width,
                  height: area.height,
                }}
              />
            );
          })}
              
              {/* Teleport Zones Layer - Commented out after debugging
              <TeleportZonesLayer
                teleportZones={teleportZones}
                cameraPos={{ x: camX, y: camY }}
              />
              */}

              {/* Character - Hidden during fishing animation */}
              {!isFishing && (
                <div
                  className="character"
                  style={{
                    left: playerPos.x - camX,
                    top: playerPos.y - camY,
                    backgroundImage: `url(${characterSpriteUrl})`,
                    backgroundPosition: `-${playerStep * 32}px -${getPlayerSpriteRowOffset(
                      direction
                    )}px`,
                  }}
                />
              )}

              {/* Floating texts */}
              {floatingTexts.map((ft) => {
                const sx = ft.x - camX,
                  sy = ft.y - camY;
                return (
                  <div
                    key={ft.id}
                    className="floating-text"
                    style={{ left: sx, top: sy, color: ft.color }}
                  >
                    <span style={{ marginRight: ft.imageUrl ? 4 : 0 }}>
                      {ft.text}
                    </span>
                    {ft.imageUrl && (
                      <img
                        src={ft.imageUrl}
                        alt=""
                        style={{ width: 16, height: 16, verticalAlign: "middle" }}
                      />
                    )}
                  </div>
                );
              })}

           
              {/* Steam effects for EmberSpa or maps with showSteamEffects */}
              {(MAP_ID === 12 || (config && config.showSteamEffects)) && steamPuffs.map((puff) => {
                const sx = puff.x - camX;
                const sy = puff.y - camY;
                const elapsed = Date.now() - puff.startTime;
                const progress = Math.min(elapsed / puff.duration, 1);

                // Steam rises and fades
                const riseDistance = progress * 24; // Rise 24px total
                const currentOpacity = puff.opacity * (1 - progress * 0.8); // Fade to 20% of original
                const currentSize = puff.size * (1 + progress * 0.5); // Grow slightly

                return (
                  <div
                    key={puff.id}
                    style={{
                      position: 'absolute',
                      left: sx,
                      top: sy - riseDistance,
                      width: currentSize,
                      height: currentSize,
                      borderRadius: '50%',
                      background: 'radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.9) 0%, rgba(240, 248, 255, 0.7) 50%, rgba(220, 230, 240, 0.4) 80%, transparent 100%)',
                      boxShadow: 'inset 0 0 2px rgba(255, 255, 255, 0.8), 0 0 3px rgba(255, 255, 255, 0.3)',
                      opacity: currentOpacity,
                      pointerEvents: 'none',
                      zIndex: 8 // Above map but below UI
                    }}
                  />
                );
              })}

              {/* Rocks Layer */}
              <RocksLayer
                rocks={rocks}
                camX={camX}
                camY={camY}
                onRockClick={handleRockClick}
                breakingFrames={breakingFrames}
              />

              {/* Berry Plants Layer - Only on Island map */}
              {MAP_ID === 1 && berryPlants.map((plant) => {
                const screenX = plant.x - camX;
                const screenY = plant.y - camY;
                
                // Only render if visible
                if (
                  screenX < -32 || screenX > VIEWPORT_WIDTH ||
                  screenY < -32 || screenY > VIEWPORT_HEIGHT
                ) {
                  return null;
                }

                // Backend already calculates sprite, frame_offset, and sprite_height
                const spriteUrl = plant.sprite;
                const frameOffset = plant.frame_offset;
                const spriteHeight = plant.sprite_height || 16;
                const spriteWidth = plant.sprite_width || 16;

                return (
                  <div
                    key={plant.id}
                    style={{
                      position: "absolute",
                      left: screenX,
                      top: screenY - (spriteHeight - 16), // Adjust Y position for taller sprites
                      width: spriteWidth,
                      height: spriteHeight,
                      backgroundImage: `url(${spriteUrl})`,
                      backgroundPosition: `-${frameOffset}px 0px`,
                      backgroundSize: `auto ${spriteHeight}px`,
                      backgroundRepeat: "no-repeat",
                      cursor: plant.is_ready ? "pointer" : "default",
                      imageRendering: "pixelated",
                      border:  "none",
                      boxSizing: "border-box",
                      zIndex: 50,  // Above most elements to ensure clickability
                      pointerEvents: "auto"  // Ensure clicks are captured
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                
                      
                      if (plant.is_ready) {
                  
                        handleHarvestClick(plant);
                      } else {
                        // Show growth percentage on click
                        spawnText(
                          `Growing: ${Math.round(plant.growth_percent)}%`,
                          plant.x,
                          plant.y - 16,
                          "#00aaff"
                        );
                      }
                    }}
                    title={plant.is_ready ? "Click to harvest!" : `Growing: ${Math.round(plant.growth_percent)}%`}
                  />
                );
              })}

              {/* Empty planting spots - Only on Island map */}
              {MAP_ID === 1 && (() => {
                // Get all 13 planting positions from backend config
                const allSlots = Array.from({ length: 24 }, (_, i) => i);
                const occupiedSlots = berryPlants.map(p => p.slot_index);
                const emptySlots = allSlots.filter(i => !occupiedSlots.includes(i));
                
                // Check if player has a berry selected
                const hasBerrySelected = selectedAction?.type === "berry";
                
   
         
                return emptySlots.map(slotIndex => {
                  const [x, y] = BERRY_PLANT_POSITIONS[slotIndex];
                  const screenX = x - camX;
                  const screenY = y - camY;
                  
                  // Only render if visible
                  if (
                    screenX < -32 || screenX > VIEWPORT_WIDTH ||
                    screenY < -32 || screenY > VIEWPORT_HEIGHT
                  ) {
                    return null;
                  }

                  // Make more visible when berry is selected
                  const isActive = hasBerrySelected;

    

                  return (
                    <div
                      key={`empty-${slotIndex}`}
                      style={{
                        position: "absolute",
                        left: screenX,
                        top: screenY,
                        width: 16,
                        height: 16,
                        // border: isActive ? "2px solid rgba(100, 255, 100, 0.8)" : "1px dashed rgba(100, 200, 100, 0.3)",
                        // backgroundColor: isActive ? "rgba(50, 255, 50, 0.2)" : "rgba(50, 150, 50, 0.05)",
                        cursor: isActive ? "pointer" : "default",
                        // boxSizing: "border-box",
                        transition: "all 0.2s ease",
                        pointerEvents: isActive ? "auto" : "none",
                        zIndex: 100  // Make sure it's above other elements
                      }}
                      onClick={(e) => {
                        console.log("🖱️ Click on slot", slotIndex, "isActive:", isActive);
                        if (isActive) {
                          handlePlantClick(slotIndex);
                        }
                      }}
                      title={isActive ? "Click to plant a berry" : "Select a berry from your pouch"}
                    />
                  );
                });
              })()}

<TrainersLayer
          trainers={trainerStates}
          camX={camX}
          camY={camY}
          playerX={playerPos.x}
          playerY={playerPos.y}
          onTrainerClick={handleTrainerClick}
        />

              <PokemonLayer
                pokemons={wildPokemon}
                camX={camX}
                camY={camY}
                onPokemonClick={handlePokemonClick}
                selectedBall={selectedBall}
              />
              
              {/* Fishing Spots - Only on Island map */}
              {MAP_ID === 1 && fishingSpots.length > 0 && !isFishing && (
                <FishingSpots
                  spots={fishingSpots}
                  onSpotClick={handleFishingSpotClick}
                  playerX={playerPos.x}
                  playerY={playerPos.y}
                />
              )}

              {/* Fishing Animation */}
              {fishingAnimation && (
                <FishingAnimation
                  x={fishingAnimation.x - camX}
                  y={fishingAnimation.y - camY}
                  direction={fishingAnimation.direction}
                  duration={fishingAnimation.duration}
                  onComplete={() => {
                    setFishingAnimation(null);
                    setIsFishing(false);
                  }}
                />
              )}
              
              {/* Portal Layer */}
              <PortalLayer
                portals={portals}
                camX={camX}
                camY={camY}
                onPortalClick={handlePortalClick}
              />
              
              {/* NPCs Layer */}
              <NPCLayer
                npcs={config.npcs || []}
                camX={camX}
                camY={camY}
                playerX={playerPos.x}
                playerY={playerPos.y}
                onNPCClick={handleNPCClick}
              />
              
              {/* Shop Lots Layer - Only shown on the player shop map */}
              {config.id === 'playerShop' && (
                <ShopLotsLayer
                  camX={camX}
                  camY={camY}
                  onShopLotClick={handleNPCClick}
                />
              )}
              
              {/* Professional game-quality capture animations */}
              {captureEffects.map(effect => {
                const screenX = effect.x - camX;
                const screenY = effect.y - camY;
                // Ball type is already mapped to CSS class name when the effect was created
                const ballTypeClass = effect.ballType || 'pokeball';
                
                // Success animations have been removed as requested
                if (effect.type === 'success') {
                  return null; // Don't render any success animations
                }
                
                // Breakout animations with ball-specific colors
                if (effect.type === 'breakout') {
                  const particleCount = ballTypeClass === 'ultraball' ? 12 : 
                                      ballTypeClass === 'greatball' ? 10 : 8;
                  
                  return (
                    <div key={effect.id} style={{position: 'absolute', left: screenX, top: screenY}}>
                      <div className={`breakout-burst ${ballTypeClass}`}></div>
                      <div className="breakout-particles">
                        {Array.from({length: particleCount}).map((_, i) => {
                          const angle = (i / particleCount) * Math.PI * 2;
                          const distance = 5 + Math.random() * 15;
                          const delay = Math.random() * 0.3;
                          const duration = 0.3 + Math.random() * 0.3;
                          const x = Math.cos(angle) * distance;
                          const y = Math.sin(angle) * distance;
                          
                          return (
                            <div 
                              key={`particle-${i}`} 
                              className={`breakout-particle ${ballTypeClass}`}
                              style={{
                                left: x + 16,
                                top: y + 16,
                                animation: `breakout-burst ${duration}s ease-out forwards`,
                                animationDelay: `${delay}s`
                              }}
                            ></div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                
                return null;
              })}
            </div>

            {/* Movement Controls - Analog Stick or D-Pad */}
            <div className="analog-stick-wrapper">
              {controlType === 'analog' ? (
                <AnalogStick onMove={handleStick} />
              ) : (
                <DPad onMove={handleDPad} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Active Lure countdown */}
      {activeLure && countdown > 0 && (
        <div
          style={{
            position: "fixed",
            bottom: "4rem",
            right: 20,
            padding: 8,
            background: "rgba(0,0,0,0.7)",
            color: "#fff",
            borderRadius: 6,
            textAlign: "center",
            zIndex: 9999,
          }}
        >
          {activeLure === "lure" && <img src={lureImg} alt="" />}
          {activeLure === "super_lure" && <img src={superLureImg} alt="" />}
          {activeLure === "ultra_lure" && <img src={ultraLureImg} alt="" />}
          <div>
            {Math.floor(countdown / 60)}m {countdown % 60}s
          </div>
        </div>
      )}

      
      {/* Mineral Potion Indicator */}
      <MineralPotionIndicator />

      {/* Shiny Potion Indicator */}
      <ShinyPotionIndicator />

      {/* NPC Dialogue Modal */}
      <DialogueModal
        isOpen={dialogueModalOpen}
        toggle={() => setDialogueModalOpen(false)}
        npc={activeNPC}
        onComplete={(npc) => {
          // Handle any post-dialogue actions here
          if (npc && npc.onComplete) {
            npc.onComplete();
          }
        }}
      />

      {/* Raffle Modal */}
      <RaffleModal
        isOpen={raffleModalOpen}
        toggle={() => setRaffleModalOpen(false)}
        playerPos={playerPos}
        spawnText={spawnText}
      />
      
      {/* Department Store Modal */}
      <DepartmentStoreModal
        isOpen={departmentStoreModalOpen}
        toggle={() => setDepartmentStoreModalOpen(false)}
        filteredItems={activeCategory === 'all' ? 
          // Only show items that should be in the department store
          storeItems.filter(item => 
            // Pokeballs
            [2, 3, 4, 4000, 4001, 4002, 4003, 4004, 4005, 4006, 4007].includes(item.id) ||
            // Map items (lures, charms)
            [214, 701780, 701781, 194, 195].includes(item.id)
          ) : 
          // Filter by category
          storeItems.filter(item => {
            if (activeCategory === 'pokeballs') {
              return [2, 3, 4, 4000, 4001, 4002, 4003, 4004, 4005, 4006, 4007].includes(item.id);
            } else if (activeCategory === 'lures') {
              return [214, 701780, 701781, 194, 195].includes(item.id);
            }
            return false;
          })}
        currency={currency}
        categories={[
          { id: 'all', name: 'All Items' },
          { id: 'pokeballs', name: 'Pokéballs' },
          { id: 'lures', name: 'Lures' },
          { id: 'items', name: 'Other Items' }
        ]}
        activeCategory={activeCategory}
        setActiveCategory={setActiveCategory}
        buyAmounts={quantityMap}
        handleQuantityChange={(id, value) => setQuantityMap(prev => ({ ...prev, [id]: value }))}
        handlePurchase={async (item) => {
          const qty = parseInt(quantityMap[item.id] || 1, 10);
          try {
            // Perform the purchase
            await api.post(
              "/v1/purchase",
              { item: item.id, quantity: qty },
              { withCredentials: true }
            );
            
            // Show success message
            spawnText(`Bought ${qty}× ${item.name}!`, playerPos.x, playerPos.y - 16, "#00ff00");
            
            // Update currency
            const currRes = await api.get("/v1/currency", { withCredentials: true });
            if (Array.isArray(currRes.data)) {
              if (currRes.data.length) setCurrency(currRes.data[0]);
            } else {
              setCurrency(currRes.data);
            }
          } catch (err) {
            const detail = err.response?.data?.detail || "Purchase failed.";
            spawnText(detail, playerPos.x, playerPos.y - 16, "red");
          }
        }}
        loadingItems={storeItems.length === 0}
        catcherExp={0}
        catcherLevel={0}
      />

      {/* Island Shop Modal */}
      {islandShopModalOpen && (
        <IslandShopModal
          onClose={() => setIslandShopModalOpen(false)}
          onPokemonChange={(slotIndex, newSpecies) => {
            // Update the Pokemon on the map immediately
            setWildPokemon(prev => {
              const updated = [...prev];
              // Find the Pokemon at this slot index (non-fishing Pokemon)
              const nonFishingPokemon = updated.filter(p => !p.id?.toString().startsWith('fishing_'));
              if (slotIndex < nonFishingPokemon.length) {
                const targetPokemon = nonFishingPokemon[slotIndex];
                const targetIndex = updated.findIndex(p => p.id === targetPokemon.id);
                if (targetIndex !== -1) {
                  updated[targetIndex] = {
                    ...updated[targetIndex],
                    species: newSpecies,
                    name: newSpecies,
                    sprite: `/Maps/Overworld/${newSpecies}.png`
                  };
                }
              }
              return updated;
            });
          }}
        />
      )}

      {/* Lucky Dip Modal */}
      <LuckyDipModal
        isOpen={luckyDipModalOpen}
        toggle={() => setLuckyDipModalOpen(false)}
        playerPos={playerPos}
        spawnText={spawnText}
      />

      {/* Collection Contest Modal */}
      <CollectionContestModal
        isOpen={collectionContestModalOpen}
        toggle={() => setCollectionContestModalOpen(false)}
        playerPos={playerPos}
        spawnText={spawnText}
      />

      {/* Auction House Modal */}
      <AuctionHouseModal
        isOpen={auctionHouseModalOpen}
        toggle={() => setAuctionHouseModalOpen(false)}
        playerPos={playerPos}
        spawnText={spawnText}
      />

<DiglettDashModal
        isOpen={diglettDashModalOpen}
        toggle={() => setDiglettDashModalOpen(false)}
        playerPos={playerPos}
        spawnText={spawnText}
      />


      {/* Held Item Quick Swap Modal */}
      <HeldItemQuickSwap
        isVisible={showHeldItemSwap}
        onClose={() => setShowHeldItemSwap(false)}
        onSwapComplete={() => {
          refreshEquippedItems(); // Refresh equipped items display
          spawnText("Held item swapped!", playerPos.x, playerPos.y - 16, "green");
        }}
      />
      {/* Racetrack Modal */}
      <RacetrackModal
        isOpen={racetrackModalOpen}
        toggle={() => setRacetrackModalOpen(false)}
        playerPos={playerPos}
        spawnText={spawnText}
      />
      {/* Wheel of Fortune Modal */}
      <WheelOfFortuneModal
        isOpen={wheelOfFortuneModalOpen}
        toggle={() => setWheelOfFortuneModalOpen(false)}
        playerPos={playerPos}
        spawnText={spawnText}
      />
      
      {/* Balloon Pop Modal */}
      <BalloonPopModal
        isOpen={balloonPopModalOpen}
        toggle={() => setBalloonPopModalOpen(false)}
        playerPos={playerPos}
        spawnText={spawnText}
      />
      
      {/* Ticket Exchange Modal */}
      <TicketExchangeModal
        isOpen={ticketExchangeModalOpen}
        toggle={() => setTicketExchangeModalOpen(false)}
        playerPos={playerPos}
        spawnText={spawnText}
      />
      
      {/* Player Shop Lot Modal */}
      <PlayerShopLotModal
        isOpen={playerShopLotModalOpen}
        toggle={() => setPlayerShopLotModalOpen(false)}
      />
      
      {/* Player Shop Browse Modal */}
      <PlayerShopBrowseModal
        isOpen={playerShopBrowseModalOpen}
        toggle={() => setPlayerShopBrowseModalOpen(false)}
        lotId={selectedLotId}
        shopName={selectedShopName}
      />
      
      {/* Player Shop Management Modal */}
      <PlayerShopManageModal
        isOpen={playerShopManageModalOpen}
        toggle={() => setPlayerShopManageModalOpen(false)}
        lotId={selectedLotId}
      />
    </div>
  );
}
